import { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo, KeyboardEvent, WheelEvent, TouchEvent } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../lib/api'
import {
  consumeSse, salvaJobRef, carregaJobRef, listaJobsAtivos, anexaJob,
  cancelaJob, escalaJob, rotuloTier, type OfertaEscalonamento,
} from '../lib/oracleAiJob'
import { GiBrain, GiBroom } from 'react-icons/gi'
import RevisaoAprendizado from './RevisaoAprendizado'
import {
  FiDatabase,
  FiDownload,
  FiClock,
  FiChevronRight,
  FiChevronDown,
  FiCheck,
  FiX,
  FiSearch,
  FiCopy,
  FiLoader,
  FiTrash2,
  FiZap,
  FiSend,
  FiFileText,
  FiCode,
  FiChevronLeft,
  FiBarChart2,
  FiArrowLeft,
  FiTrendingUp,
  FiHash,
  FiUsers,
  FiPercent,
  FiPlus,
  FiEdit2,
  FiMessageSquare,
  FiAlertTriangle,
  FiThumbsUp,
  FiThumbsDown,
  FiAward,
  FiFolderPlus,
  FiPlusCircle,
  FiCommand,
  FiMic,
  FiSquare,
  FiMoreVertical,
} from 'react-icons/fi'
import { LuBanknote } from 'react-icons/lu'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import { CHART_COLORS as NEU_CHART, GRID } from '../lib/chartTheme'

// ─── SQL Highlight Tokenizer ───
const SQL_KW = new Set(['SELECT','FROM','WHERE','AND','OR','NOT','IN','EXISTS','BETWEEN','LIKE','IS','NULL','AS','ON','JOIN','LEFT','RIGHT','INNER','OUTER','FULL','CROSS','UNION','ALL','DISTINCT','ORDER','BY','GROUP','HAVING','WITH','CASE','WHEN','THEN','ELSE','END','ROWNUM','COUNT','SUM','AVG','MAX','MIN','NVL','DECODE','TO_CHAR','TO_NUMBER','TO_DATE','SUBSTR','TRIM','UPPER','LOWER','ROUND','TRUNC','DESC','ASC','OVER','PARTITION','ROW_NUMBER','RANK','DENSE_RANK'])
function esc(s: string) { return s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;') }
function hlSQL(sql: string): string {
  if (!sql) return ''
  const out: string[] = []; let i = 0
  while (i < sql.length) {
    if (sql[i]==='-'&&sql[i+1]==='-') { const e=sql.indexOf('\n',i); const end=e<0?sql.length:e; out.push(`<span class="text-emerald-600 italic">${esc(sql.slice(i,end))}</span>`); i=end; continue }
    if (sql[i]==="'") { let j=i+1; while(j<sql.length&&sql[j]!=="'")j++; out.push(`<span class="text-amber-400">${esc(sql.slice(i,j+1))}</span>`); i=j+1; continue }
    if (/[a-zA-Z_]/.test(sql[i])) { let j=i; while(j<sql.length&&/[a-zA-Z0-9_$#]/.test(sql[j]))j++; const w=sql.slice(i,j); out.push(SQL_KW.has(w.toUpperCase())?`<span class="text-blue-400 font-semibold">${esc(w.toUpperCase())}</span>`:esc(w)); i=j; continue }
    if (/\d/.test(sql[i])) { let j=i; while(j<sql.length&&/[\d.]/.test(sql[j]))j++; out.push(`<span class="text-green-400">${esc(sql.slice(i,j))}</span>`); i=j; continue }
    out.push(esc(sql[i])); i++
  }
  return out.join('')
}

// Identificação curta do SQL para a barra da bolha do chat. Contar linhas não
// serve: o modelo devolve o SELECT inteiro numa linha só, então seria sempre
// "1 linha". O começo da query é o que de fato distingue um turno do outro.
function resumoSQL(sql: string, max = 48): string {
  const limpo = (sql || '').replace(/\s+/g, ' ').trim()
  return limpo.length > max ? limpo.slice(0, max) + '…' : limpo
}

// ─── Types ───
type FormatHint = 'currency' | 'date' | 'percent' | 'semana' | 'phone' | 'cep' | 'cpf' | 'cnpj' | 'id' | 'number' | 'text'
interface QueryColumn { name: string; type: string; formatHint?: FormatHint }
interface QueryResult { columns: QueryColumn[]; rows: Record<string, unknown>[]; rowCount: number; maxRowsReached: boolean; executionTimeMs: number; historyId?: number }
interface QueryHistoryItem { id: number; user_id?: string; query_text: string; user_prompt?: string | null; execution_time_ms: number | null; row_count: number | null; status: 'success' | 'error'; error_message: string | null; created_at: string; label: string | null; has_report: number; has_conversation: number; username?: string | null; full_name?: string | null; avatar_url?: string | null; is_admin?: 0 | 1 }
// Resultado do turno, anexado à mensagem do assistente DEPOIS que o SQL roda.
// Sem isto o modelo recebia o SQL do turno anterior mas nunca o que ele
// devolveu — era por isso que "quem foram" perdia o filtro do agregado e que
// "você disse 12 e agora 19?" não tinha como ser respondido (26/07/2026).
interface AiTurnResult { rowCount: number; columns: string[]; firstRow?: string }
interface AiMessage { id: string; role: 'user' | 'assistant' | 'error' | 'system'; content: string; sql?: string; result?: AiTurnResult; timestamp: Date; validatedQueryId?: number | null; userVote?: 1 | -1 | null; promoted?: boolean; justPromoted?: boolean; votesUp?: number; votesDown?: number; vaultSlug?: string | null; thinking?: string; authorId?: string; authorName?: string; authorAvatar?: string | null }

// Linha "-- resultado: ..." que acompanha o SQL no histórico enviado ao modelo.
// TETO RÍGIDO de 400 chars por turno: o prompt mediano medido é 85.004 chars e
// ORACLE_AI_PROMPT_BUDGET é 100.000 — 6 turnos × 400 = 2,4k cabem sem encostar
// no trimmer do backend, que descartaria justamente os turnos mais antigos.
const HIST_RESULT_MAX = 400
function formatTurnResult(r?: AiTurnResult): string {
  if (!r) return ''
  const partes = [`${r.rowCount} linha(s)`]
  if (r.columns?.length) partes.push(`colunas: ${r.columns.slice(0, 8).join(', ')}`)
  if (r.firstRow) partes.push(`1ª linha: ${r.firstRow}`)
  return `\n-- resultado: ${partes.join(' | ')}`.slice(0, HIST_RESULT_MAX)
}
interface ReportKPI { label: string; value: string; icon?: string; color?: string }
interface ReportChart { type: 'bar' | 'area' | 'pie' | 'horizontal_bar'; title: string; dataKey: string; categoryKey: string; data: Record<string, unknown>[]; color?: string }
interface ReportData { title: string; summary: string; kpis: ReportKPI[]; charts: ReportChart[] }
interface QCVariable { key: string; label: string; type: 'text' | 'select'; default: string; placeholder?: string; options?: { label: string; value: string }[] }
interface UserQuickCommand { id: number; label: string; description: string; icon: string; sql_template: string; variables: QCVariable[]; sort_order: number; use_count: number }

const CHART_COLORS = NEU_CHART // paleta unica e validada: lib/chartTheme.ts

// Palette estável por autor — cor é função do authorId (Ricardo sempre azul,
// Juliane sempre roxo, etc). `name` tem variante para cada tema porque tons
// -400 caem ilegíveis em fundo claro; usamos -700 no light e -300 no dark.
// Borda da bolha mantém alpha baixo (decorativa, não carrega texto).
const AUTHOR_PALETTE = [
  { bg: 'bg-blue-600',    text: 'text-white', name: 'text-blue-700 dark:text-blue-300',     border: 'border-blue-500/60'    },
  { bg: 'bg-purple-600',  text: 'text-white', name: 'text-purple-700 dark:text-purple-300', border: 'border-purple-500/60'  },
  { bg: 'bg-rose-600',    text: 'text-white', name: 'text-rose-700 dark:text-rose-300',     border: 'border-rose-500/60'    },
  { bg: 'bg-cyan-700',    text: 'text-white', name: 'text-cyan-700 dark:text-cyan-300',     border: 'border-cyan-500/60'    },
  { bg: 'bg-orange-600',  text: 'text-white', name: 'text-orange-700 dark:text-orange-300', border: 'border-orange-500/60'  },
  { bg: 'bg-pink-600',    text: 'text-white', name: 'text-pink-700 dark:text-pink-300',     border: 'border-pink-500/60'    },
  { bg: 'bg-indigo-600',  text: 'text-white', name: 'text-indigo-700 dark:text-indigo-300', border: 'border-indigo-500/60'  },
  { bg: 'bg-teal-700',    text: 'text-white', name: 'text-teal-700 dark:text-teal-300',     border: 'border-teal-500/60'    },
]
const OWN_PALETTE = { bg: 'bg-emerald-600', text: 'text-white', name: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-500/60' }
function hashStr(s: string): number { let h = 0; for (let i = 0; i < s.length; i++) { h = (h * 31 + s.charCodeAt(i)) | 0 } return Math.abs(h) }
function pickAuthorPalette(authorId: string | undefined, isOwn: boolean) {
  if (isOwn) return OWN_PALETTE
  if (!authorId) return AUTHOR_PALETTE[0]
  return AUTHOR_PALETTE[hashStr(authorId) % AUTHOR_PALETTE.length]
}
function authorInitials(name: string | undefined): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// Clara consulta o Oracle via service-user `moss-bot` (OracleQueryTool do moss-v2),
// logando no MESMO oracle_query_history. Detectamos pelo username pra exibir como
// "Clara" (não "moss-bot") na árvore de histórico.
const CLARA_USERNAME = 'moss-bot'
function isClaraItem(it: { username?: string | null }): boolean { return it.username === CLARA_USERNAME }
function authorDisplayName(it: { username?: string | null; full_name?: string | null }): string {
  if (isClaraItem(it)) return 'Clara'
  return it.full_name || it.username || 'Usuário'
}

// Bucket de período pra agrupar o histórico na árvore (autor → período).
function periodBucket(createdAt: string | null | undefined): { key: string; label: string; order: number } {
  if (!createdAt) return { key: 'antigo', label: 'Mais antigo', order: 4 }
  const now = new Date()
  const d = new Date(createdAt)
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const t = d.getTime()
  if (t >= startToday) return { key: 'hoje', label: 'Hoje', order: 0 }
  if (t >= startToday - 86400000) return { key: 'ontem', label: 'Ontem', order: 1 }
  if (t >= startToday - 7 * 86400000) return { key: 'semana', label: 'Últimos 7 dias', order: 2 }
  if (t >= startToday - 30 * 86400000) return { key: 'mes', label: 'Últimos 30 dias', order: 3 }
  return { key: 'antigo', label: 'Mais antigo', order: 4 }
}

const KPI_ICONS: Record<string, typeof FiTrendingUp> = {
  'trending-up': FiTrendingUp, 'hash': FiHash, 'users': FiUsers,
  'dollar': LuBanknote, 'percent': FiPercent, 'bar-chart': FiBarChart2
}

// Pluralizador simples PT-BR para contagens: pluralize(3, 'linha', 'linhas')
// Aceita 0 também ("0 linhas") sem precisar de caso especial.
function pluralize(n: number, singular: string, plural: string): string {
  return `${n.toLocaleString('pt-BR')} ${n === 1 ? singular : plural}`
}

// ─── Tradução de erros pra diretoria (sem jargão técnico) ───
// Mapeia ORA-xxxxx mais comuns do Oracle 11g pra mensagens em PT-BR.
// O painel mostra essa versão; a string crua fica no console pra TI.
const ORA_ERROR_MAP: Record<string, string> = {
  'ORA-00904': 'A consulta referencia uma coluna que não existe. Tente reformular.',
  'ORA-00942': 'A consulta referencia uma tabela inexistente ou sem permissão.',
  'ORA-00936': 'A consulta está incompleta — a IA não terminou de montá-la.',
  'ORA-00933': 'Comando SQL terminou de forma inesperada.',
  'ORA-00918': 'A consulta tem coluna ambígua entre tabelas — precisa qualificar com alias.',
  'ORA-00979': 'A consulta agrega valores sem agrupar todas as colunas.',
  'ORA-01017': 'Credenciais do Oracle inválidas — contate TI.',
  'ORA-01036': 'A consulta tem variável incorreta — a IA precisa corrigir o SQL.',
  'ORA-01722': 'A consulta tenta converter texto em número inválido.',
  'ORA-01756': 'String não fechada na consulta.',
  'ORA-01789': 'Número de colunas diferente entre UNION/INTERSECT/MINUS.',
  'ORA-01843': 'Data inválida — formato esperado pode ser DD/MM/AAAA.',
  'ORA-01861': 'Formato de data não bate com o esperado pelo Oracle.',
  'ORA-01874': 'Hora fora do intervalo permitido (00-23).',
  'ORA-01927': 'Privilégio insuficiente no Oracle — contate TI.',
  'ORA-12541': 'Servidor Oracle (DRACO) inacessível — verifique rede/firewall.',
  'ORA-12545': 'Não foi possível conectar ao Oracle (DNS/host). Contate TI.',
  'ORA-12170': 'Tempo esgotado ao conectar no Oracle. Tente novamente em instantes.',
  'NJS-040': 'O Oracle está congestionado. Tente novamente em alguns segundos.',
  'NJS-500': 'Conexão com Oracle perdida. Tentando reconectar...',
}
export function translateOracleError(raw: string | null | undefined): { friendly: string; code: string | null } {
  if (!raw) return { friendly: 'Erro desconhecido.', code: null }
  const m = raw.match(/(ORA-\d{5}|NJS-\d{3})/)
  const code = m ? m[1] : null
  if (code && ORA_ERROR_MAP[code]) return { friendly: ORA_ERROR_MAP[code], code }
  if (code) return { friendly: `Oracle retornou ${code}. TI foi notificado.`, code }
  // Fallback amigável: extrai 1ª linha, remove pilha/binds
  const firstLine = raw.split('\n')[0].slice(0, 200)
  return { friendly: firstLine, code: null }
}

// Mapeia erros de transporte/rede pra mensagens claras.
// `code` é um discriminador estável que TI pode consultar nos logs.
function translateRequestError(e: unknown): string {
  if (e instanceof DOMException) {
    if (e.name === 'AbortError') return 'Operação cancelada.'
    if (e.name === 'TimeoutError') return 'Tempo esgotado — o servidor demorou demais a responder. Tente novamente.'
  }
  if (e instanceof TypeError && /failed to fetch|networkerror/i.test(e.message)) {
    return 'Sem conexão com o servidor. Verifique sua internet ou contate TI.'
  }
  if (e instanceof Error) {
    const msg = e.message
    // Padrões discriminados que o backend pode emitir via event: error
    if (/ollama|llm|gemma|qwen/i.test(msg)) return 'Assistente IA indisponível no momento. Tente em instantes.'
    if (/oracle|draco|NJS-|ORA-/i.test(msg)) return translateOracleError(msg).friendly
    if (/explain.plan|dry.?run/i.test(msg)) return 'A IA não conseguiu validar a consulta. Tentando novamente...'
    if (/timeout|esgotad/i.test(msg)) return 'Tempo esgotado. Tente novamente.'
    return msg.slice(0, 200)
  }
  return 'Erro inesperado.'
}

export function InfrastructureDBPanel() {
  const { authenticatedFetch, user } = useAuth()

  const [connectionStatus, setConnectionStatus] = useState<'unknown' | 'connected' | 'error'>('unknown')
  const [connectionInfo, setConnectionInfo] = useState('')
  const [isTestingConnection, setIsTestingConnection] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(true)


  const [currentSQL, setCurrentSQL] = useState('')
  const [isExecuting, setIsExecuting] = useState(false)
  const [queryResult, setQueryResult] = useState<QueryResult | null>(null)
  const [sortCol, setSortCol] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [queryError, setQueryError] = useState<string | null>(null)
  // Feedback do botão de copiar SQL do bloco preto (check verde por 1.5s).
  const [sqlCopied, setSqlCopied] = useState(false)

  // Histórico de geração de queries — agora é a ÁRVORE LATERAL (autor → período)
  // de TODOS os admins + a Clara (service-user moss-bot), substituindo a antiga
  // árvore de schemas Oracle. Atualiza viva (polling) enquanto o painel está aberto.
  const [queryHistory, setQueryHistory] = useState<QueryHistoryItem[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [histExpandedAuthors, setHistExpandedAuthors] = useState<Record<string, boolean>>({})
  const [histExpandedPeriods, setHistExpandedPeriods] = useState<Record<string, boolean>>({})
  const [activeHistoryId, setActiveHistoryId] = useState<number | null>(null)
  // SQL original do item de histórico aberto — ver executeSQL (reexecução).
  const activeHistorySqlRef = useRef<string>('')
  // Qual bolha teve o SQL copiado agora (feedback ✓ por 1,5s).
  const [copiedMsgId, setCopiedMsgId] = useState<string | null>(null)
  // Metadados do item carregado no centro (pra mostrar prompt/autor no card sem reexecutar).
  const [activeMeta, setActiveMeta] = useState<{ prompt: string | null; authorName: string | null; createdAt: string | null; status: 'success' | 'error' } | null>(null)
  const [histFilter, setHistFilter] = useState('')
  const didAutoLoadHistory = useRef(false)

  // humanize: formata DATE/NUMBER/SEMANA visualmente (R$ 1.234,56, DD/MM/AAAA).
  // Persistido em localStorage pra preferência sobreviver a reload. Default
  // false (RAW) na primeira visita, conservador pra debug.
  const [humanize, setHumanize] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return localStorage.getItem('oracle.humanize') === '1'
  })
  useEffect(() => {
    try { localStorage.setItem('oracle.humanize', humanize ? '1' : '0') } catch { /* */ }
  }, [humanize])
  const [chatOpen, setChatOpen] = useState(true)
  const [reportView, setReportView] = useState(false)
  const [reportData, setReportData] = useState<ReportData | null>(null)
  const [reportLoading, setReportLoading] = useState(false)

  const [aiMessages, setAiMessages] = useState<AiMessage[]>([])
  const [aiInput, setAiInput] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  // Default antes da 1ª resposta do backend. Cada chamada de IA sobrescreve com
  // d.modelUsed (hoje "Claude Sonnet 4.6 (SDK)" — Ollama removido em 2026-05-29,
  // Claude virou provider único). Ver vault/pipeline/2026-05-29-remocao-ollama-claude-only.md.
  // Camada 5: quando a IA gera SQL e o backend já tentou N vezes corrigir
  // o zero-rows sem sucesso, o frontend exibe um aviso específico em vez do
  // guia genérico de "Causas comuns no DRACO". Reseta a cada nova query.
  const [lastAiZeroRowAttempts, setLastAiZeroRowAttempts] = useState<number>(0)
  const [lastAiZeroRowFinal, setLastAiZeroRowFinal] = useState<boolean>(false)
  const [aiStage, setAiStage] = useState<{ step: number; label: string } | null>(null)
  // Camada 8 (SSE streaming): texto de thinking acumulado em tempo real durante a geração.
  // Limpo a cada nova request. Espelhado no AiMessage final em assistantMsg.thinking.
  const [streamingThinking, setStreamingThinking] = useState<string>('')
  const [thinkingOpen, setThinkingOpen] = useState<boolean>(true)
  // Camada 8.6: contexto injetado no system prompt (emitido pelo evento SSE
  // 'context' logo antes do thinking começar). Mostrado como chips clicáveis
  // pra abrir a nota original no vault em nova aba.
  const [injectedContext, setInjectedContext] = useState<{ vaultTables: string[]; learnedSlugs: string[] }>({ vaultTables: [], learnedSlugs: [] })
  const aiChatRef = useRef<HTMLDivElement>(null)
  const aiInputRef = useRef<HTMLTextAreaElement>(null)
  // ── Entrada por voz (tablets): mic no lugar do enviar quando a box está
  // vazia/sem foco. Gravação espelhada do WhisperLab; transcreve no Whisper
  // self-hosted e envia direto. SEM prompt no whisper (fabrica palavras).
  const [recState, setRecState] = useState<'idle' | 'recording' | 'transcribing'>('idle')
  const [recSecs, setRecSecs] = useState(0)
  const [recError, setRecError] = useState<string | null>(null)
  const [composerFocused, setComposerFocused] = useState(false)
  // ── Tela estreita (celular/tablet): abaixo de 1280px o painel deixa de ser
  // três colunas e vira uma tela de conversa. 1280 é o `xl` do Tailwind, e o
  // corte foi escolhido para pegar o iPad nas DUAS posições (820 em pé, 1180
  // deitado) — em `lg` (1024) o iPad deitado continuaria no layout de desktop,
  // que é justamente o que estava ruim. Acima de 1280 nada muda.
  // O histórico vira gaveta e o resultado vira folha de tela cheia; as duas
  // reaproveitam o MESMO JSX das colunas, só trocando de posicionamento — daí
  // não haver marcação duplicada aqui.
  const [estreito, setEstreito] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 1279px)').matches)
  const [histDrawer, setHistDrawer] = useState(false)   // gaveta do histórico
  const [folhaAberta, setFolhaAberta] = useState(false) // folha do resultado
  const [menuMobile, setMenuMobile] = useState(false)   // ⋯ da barra de topo
  const painelRef = useRef<HTMLDivElement>(null)
  // `estreito` por ref: o openHistoryItem é um useCallback estável e entra nas
  // dependências de efeitos; lê-lo do estado obrigaria a recriá-lo a cada giro
  // de tablet, e com ele toda a cadeia que depende dele.
  const estreitoRef = useRef(false)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const recChunksRef = useRef<Blob[]>([])
  const recTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // ref sempre com a versão fresca do generateSqlFromAi — o onstop do
  // MediaRecorder é um closure antigo e chamaria uma versão defasada.
  const generateRef = useRef<(text?: string) => void>(() => {})
  const userScrolledUp = useRef(false)
  // Controla cancelamento do stream SSE em flight. Abortar antes de iniciar
  // novo request evita que respostas de duas perguntas se misturem (race
  // condition crítica — diretoria via SQL de query A com resultado de B).
  const aiAbortCtrlRef = useRef<AbortController | null>(null)
  const aiRequestIdRef = useRef(0)
  const [aiElapsedMs, setAiElapsedMs] = useState(0)
  // ─── Job em segundo plano ───
  // O pipeline vive no servidor, não neste componente. Consequência importante:
  // aiAbortCtrlRef passou a significar "parar de ESCUTAR" — quem para o TRABALHO
  // é cancelaJob() no backend. Desmontar o painel não cancela mais nada.
  const aiJobIdRef = useRef<string | null>(null)
  const aiLastSeqRef = useRef(0)
  // Espelho de aiMessages para callbacks que não podem tê-lo nas dependências
  // (ver aplicaResultadoJob: o effect de re-anexação depende dele).
  const aiMessagesRef = useRef<AiMessage[]>([])
  const [escalateOffer, setEscalateOffer] = useState<OfertaEscalonamento | null>(null)
  const [aiTier, setAiTier] = useState(0)
  const [escalando, setEscalando] = useState(false)

  // ─── Dynamic Quick Commands (per-user) ───
  const [userQuickCmds, setUserQuickCmds] = useState<UserQuickCommand[]>([])
  const [qcLoading, setQcLoading] = useState(false)
  const [expandedQC, setExpandedQC] = useState<number | null>(null)
  const [qcParams, setQcParams] = useState<Record<number, Record<string, string>>>({})
  const [savingQC, setSavingQC] = useState(false)
  const [deepLearning, setDeepLearning] = useState(false)
  // [2026-09-29] Revisão em lote do que a conversa aceitou (só admin).
  // Ver RevisaoAprendizado.tsx e lib/aprendizado-conversa.cjs no backend.
  const [revisaoAberta, setRevisaoAberta] = useState(false)
  const [revisaoN, setRevisaoN] = useState(0)
  useEffect(() => {
    if (!user?.isAdmin) return
    authenticatedFetch('/api/oracle/aprendizado/candidatas?contagem=1')
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d && typeof d.aceitas === 'number') setRevisaoN(d.aceitas) })
      .catch(() => {})
  }, [user?.isAdmin, authenticatedFetch])
  const [deepLearnDone, setDeepLearnDone] = useState(false)
  const [brainStatus, setBrainStatus] = useState<'green' | 'yellow' | 'red' | null>(null)
  const [brainMeta, setBrainMeta] = useState<{ queriesSinceLast: number; daysSinceLast: number | null; lastLearnedAt: string | null; neverLearned: boolean } | null>(null)
  const [showSaveQC, setShowSaveQC] = useState(false)
  const [saveQCData, setSaveQCData] = useState<{ label: string; description: string; icon: string; sqlTemplate: string; variables: QCVariable[] } | null>(null)
  // editingQCId !== null reaproveita o modal de save pra editar um comando existente.
  // null = criação (POST); id = edição (PATCH no mesmo recurso).
  const [editingQCId, setEditingQCId] = useState<number | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<{ id: number; label: string } | null>(null)
  const [showQCDropdown, setShowQCDropdown] = useState(false)
  const [qcDropdownFilter, setQcDropdownFilter] = useState('')
  const qcDropdownRef = useRef<HTMLDivElement>(null)

  const loadQuickCommands = useCallback(async () => {
    setQcLoading(true)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.ORACLE_QUICK_COMMANDS)
      const d = await r.json()
      if (d.success) {
        setUserQuickCmds(d.commands)
        // Init params with defaults
        const init: Record<number, Record<string, string>> = {}
        d.commands.forEach((cmd: UserQuickCommand) => {
          init[cmd.id] = {}
          ;(cmd.variables || []).forEach((v: QCVariable) => { init[cmd.id][v.key] = v.default })
        })
        setQcParams(init)
      }
    } catch { /* */ }
    finally { setQcLoading(false) }
  }, [authenticatedFetch])

  useEffect(() => {
    if (connectionStatus === 'connected') {
      loadQuickCommands()
      loadBrainStatus()
    }
  }, [connectionStatus])

  const buildQCSQL = useCallback((cmd: UserQuickCommand, params: Record<string, string>) => {
    let sql = cmd.sql_template
    for (const v of cmd.variables || []) {
      const val = params[v.key] ?? v.default
      sql = sql.replace(new RegExp(`\\{\\{${v.key}\\}\\}`, 'g'), val)
    }
    return sql
  }, [])

  const deleteQC = useCallback(async (id: number) => {
    try {
      await authenticatedFetch(`${API_ENDPOINTS.ORACLE_QUICK_COMMANDS}/${id}`, { method: 'DELETE' })
      setUserQuickCmds(p => p.filter(c => c.id !== id))
      // Se o modal de edit estava aberto pro mesmo comando que foi deletado,
      // fecha pra não ficar editando ID inexistente. Caso editingQCId != id
      // (modal de outro cmd ou de criação), preserva o estado.
      setEditingQCId(prev => {
        if (prev === id) {
          setShowSaveQC(false)
          setSaveQCData(null)
          return null
        }
        return prev
      })
    } catch { /* */ }
  }, [authenticatedFetch])

  const handleEditQC = useCallback((cmd: UserQuickCommand) => {
    setSaveQCData({
      label: cmd.label,
      description: cmd.description || '',
      icon: cmd.icon || '⚡',
      sqlTemplate: cmd.sql_template,
      variables: (cmd.variables || []).map(v => ({ ...v }))
    })
    setEditingQCId(cmd.id)
    setExpandedQC(null)
    setShowSaveQC(true)
  }, [])

  const closeSaveQCModal = useCallback(() => {
    setShowSaveQC(false)
    setSaveQCData(null)
    setEditingQCId(null)
  }, [])

  const trackQCUsage = useCallback(async (id: number) => {
    try { await authenticatedFetch(`${API_ENDPOINTS.ORACLE_QUICK_COMMANDS}/${id}/use`, { method: 'POST' }) } catch { /* */ }
  }, [authenticatedFetch])

  const computeBrainStatus = useCallback((meta: { queriesSinceLast: number; daysSinceLast: number | null; neverLearned: boolean }) => {
    if (meta.neverLearned) return 'red' as const
    const q = meta.queriesSinceLast
    const d = meta.daysSinceLast ?? 0
    if (d > 14 || q > 40) return 'red' as const
    if (d > 3 || q > 15) return 'yellow' as const
    return 'green' as const
  }, [])

  const loadBrainStatus = useCallback(async () => {
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.ORACLE_LEARN_STATUS)
      const d = await r.json()
      if (d.success) {
        setBrainMeta({ queriesSinceLast: d.queriesSinceLast, daysSinceLast: d.daysSinceLast, lastLearnedAt: d.lastLearnedAt ?? null, neverLearned: d.neverLearned })
        setBrainStatus(computeBrainStatus(d))
      }
    } catch { /* */ }
  }, [authenticatedFetch, computeBrainStatus])

  const deepLearnTimerRef = useRef<number | null>(null)
  const handleDeepLearn = useCallback(async () => {
    if (deepLearning) return
    setDeepLearning(true)
    setDeepLearnDone(false)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.ORACLE_DEEP_LEARN, { method: 'POST' })
      const d = await r.json()
      if (d.success) {
        setDeepLearnDone(true)
        setBrainStatus('green')
        setBrainMeta({ queriesSinceLast: 0, daysSinceLast: 0, lastLearnedAt: new Date().toISOString(), neverLearned: false })
        if (deepLearnTimerRef.current) window.clearTimeout(deepLearnTimerRef.current)
        deepLearnTimerRef.current = window.setTimeout(() => {
          setDeepLearnDone(false); deepLearnTimerRef.current = null
        }, 4000)
      }
    } catch { /* */ }
    finally { setDeepLearning(false) }
  }, [deepLearning, authenticatedFetch])
  useEffect(() => () => {
    if (deepLearnTimerRef.current) window.clearTimeout(deepLearnTimerRef.current)
  }, [])

  const handleSaveAsQC = useCallback(async () => {
    if (!currentSQL) return
    setSavingQC(true)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.ORACLE_QUICK_COMMANDS_DETECT, {
        method: 'POST',
        body: JSON.stringify({ sql: currentSQL, userPrompt: aiMessages.filter(m => m.role === 'user').pop()?.content || '' })
      })
      const d = await r.json()
      if (d.success) {
        setSaveQCData({ label: d.suggestedLabel, description: '', icon: '⚡', sqlTemplate: d.sqlTemplate, variables: d.variables || [] })
        // Garante que essa abertura é criação (POST), nunca herda editingQCId
        // de uma sessão de edit anterior — defensive contra paths que pulem
        // closeSaveQCModal entre uma operação e outra.
        setEditingQCId(null)
        setShowSaveQC(true)
      }
    } catch { /* */ }
    finally { setSavingQC(false) }
  }, [currentSQL, aiMessages, authenticatedFetch])

  const confirmSaveQC = useCallback(async () => {
    if (!saveQCData) return
    try {
      const isEdit = editingQCId !== null
      const url = isEdit
        ? `${API_ENDPOINTS.ORACLE_QUICK_COMMANDS}/${editingQCId}`
        : API_ENDPOINTS.ORACLE_QUICK_COMMANDS
      const r = await authenticatedFetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        body: JSON.stringify({ label: saveQCData.label, description: saveQCData.description, icon: saveQCData.icon, sql_template: saveQCData.sqlTemplate, variables: saveQCData.variables })
      })
      const d = await r.json()
      if (d.success) {
        closeSaveQCModal()
        loadQuickCommands() // Refresh list
      }
    } catch { /* */ }
  }, [saveQCData, editingQCId, authenticatedFetch, loadQuickCommands, closeSaveQCModal])

  // ─── Auto-scroll: a última interação inteira, sempre ───
  // Dois defeitos moravam aqui e cada um produzia um sintoma:
  //
  // 1. O scroll suave se auto-cancelava. scrollTo({behavior:'smooth'}) dispara
  //    onScroll durante toda a animação, e enquanto ela estava a caminho a
  //    distância até o fim ainda era > 80px — o handler concluía "o usuário
  //    rolou para cima" e travava o auto-scroll dali em diante. Agora só o
  //    GESTO do usuário (roda/arraste) liga esse estado; o onScroll apenas o
  //    desliga ao chegar no fim. Scroll programático não tem como se sabotar.
  //
  // 2. Media a altura cedo demais. O efeito roda no commit de aiMessages, mas a
  //    bolha do assistente é Markdown (ReactMarkdown + rehypeHighlight) e só
  //    ganha altura depois — o scrollHeight daquele instante é menor que o real
  //    e a última mensagem ficava cortada. O ResizeObserver abaixo reancora
  //    quando a altura muda de verdade.
  const scrollChatToBottom = useCallback((smooth: boolean) => {
    const el = aiChatRef.current
    if (!el) return
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' })
  }, [])

  // Abrir uma conversa injeta N mensagens de uma vez: aí o fim tem que ser o
  // ponto de PARTIDA, não o destino de uma animação que começa no topo — era
  // isso que aparecia como "carregou exibindo dados acima". Salto de 1 é turno
  // normal, esse sim animado. E uma carga nova zera o "parei pra ler" anterior.
  const prevMsgCount = useRef(0)
  useEffect(() => {
    const salto = aiMessages.length - prevMsgCount.current
    prevMsgCount.current = aiMessages.length
    if (salto > 1 || aiMessages.length === 0) userScrolledUp.current = false
    if (!userScrolledUp.current) scrollChatToBottom(salto === 1)
  }, [aiMessages, aiLoading, scrollChatToBottom])

  // Reancora enquanto o conteúdo cresce (markdown, tabelas, blocos de código).
  // Sem smooth: aqui o objetivo é acompanhar o crescimento, não animar.
  useEffect(() => {
    const el = aiChatRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => {
      if (userScrolledUp.current) return
      const e = aiChatRef.current
      if (e) e.scrollTop = e.scrollHeight
    })
    Array.from(el.children).forEach(c => ro.observe(c))
    return () => ro.disconnect()
  }, [aiMessages])

  const handleChatScroll = useCallback(() => {
    const el = aiChatRef.current
    if (!el) return
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80
    // Só DESLIGA. Ligar é prerrogativa do gesto do usuário (ver handleChatWheel),
    // senão o scroll suave programático volta a se cancelar sozinho.
    if (atBottom) userScrolledUp.current = false
  }, [])

  // A intenção de "parei para ler" é declarada pelo gesto, não inferida da
  // posição: roda para cima, ou arraste para baixo no touch (que sobe o conteúdo).
  const handleChatWheel = useCallback((e: WheelEvent<HTMLDivElement>) => {
    if (e.deltaY < 0) userScrolledUp.current = true
  }, [])
  const touchStartY = useRef(0)
  const handleChatTouchStart = useCallback((e: TouchEvent<HTMLDivElement>) => {
    touchStartY.current = e.touches[0]?.clientY ?? 0
  }, [])
  const handleChatTouchMove = useCallback((e: TouchEvent<HTMLDivElement>) => {
    const y = e.touches[0]?.clientY ?? 0
    if (y - touchStartY.current > 8) userScrolledUp.current = true
  }, [])

  useEffect(() => { testConnection() }, [])

  const testConnection = useCallback(async () => {
    setIsTestingConnection(true)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.ORACLE_TEST_CONNECTION); const d = await r.json()
      if (d.success) { setConnectionStatus('connected'); setConnectionInfo(`${d.dbName} @ ${d.serverHost} (pool: ${d.poolOpen}/${d.poolInUse})`) }
      else { setConnectionStatus('error'); setConnectionInfo(d.error || 'Falha') }
    } catch { setConnectionStatus('error'); setConnectionInfo('Erro de rede') }
    finally { setIsTestingConnection(false) }
  }, [authenticatedFetch])


  // Última bolha do assistente com SQL — é nela que o cartão do resultado mora.
  const idxUltimoSql = useMemo(() => {
    for (let i = aiMessages.length - 1; i >= 0; i--) {
      if (aiMessages[i].role === 'assistant' && aiMessages[i].sql) return i
    }
    return -1
  }, [aiMessages])

  const executingSQLRef = useRef(false)
  const executeSQL = useCallback(async (sql: string, userPrompt?: string, extra?: { conversationJson?: string; aiExplanation?: string }) => {
    if (!sql.trim() || executingSQLRef.current) return null
    executingSQLRef.current = true
    setCurrentSQL(sql); setIsExecuting(true); setQueryError(null); setQueryResult(null); setReportView(false); setReportData(null); setSortCol(null)
    try {
      // Reexecutar o MESMO SQL de um item já no histórico atualiza aquela linha
      // em vez de criar outra. Sem isto, cada clique em "Executar" gerava uma
      // linha sem conversation_json que virava a mais recente do histórico — e,
      // ao ser aberta, fazia o backend sintetizar uma conversa que nunca houve.
      // A comparação com o SQL do item aberto é o que impede um quick command
      // (SQL diferente, mesmo activeHistoryId) de sobrescrever a linha errada.
      const ehReexecucao = !!activeHistoryId && !extra?.conversationJson
        && sql.trim() === (activeHistorySqlRef.current || '').trim()
      const r = await authenticatedFetch(API_ENDPOINTS.ORACLE_QUERY, { method: 'POST', body: JSON.stringify({ sql, maxRows: 50000, userPrompt: userPrompt || undefined, conversationJson: extra?.conversationJson || undefined, aiExplanation: extra?.aiExplanation || undefined, historyId: ehReexecucao ? activeHistoryId : undefined }) })
      const d = await r.json()
      if (d.success) {
        setQueryResult(d)
        setBrainMeta(prev => {
          if (!prev) return prev
          const updated = { ...prev, queriesSinceLast: prev.queriesSinceLast + 1 }
          setBrainStatus(computeBrainStatus(updated))
          return updated
        })
        // Devolve o resumo pro chamador anexar à bolha do assistente. É este
        // resumo que vai no histórico do próximo turno (ver formatTurnResult).
        const cols: string[] = (d.columns || []).map((c: { name: string }) => c.name)
        const r0 = d.rows?.[0]
        const firstRow = r0
          ? cols.slice(0, 6).map(c => `${c}=${String(r0[c] ?? '')}`.slice(0, 40)).join(', ')
          : undefined
        return { rowCount: d.rowCount ?? d.rows?.length ?? 0, columns: cols, firstRow } as AiTurnResult
      } else {
        const t = translateOracleError(d.error || 'Erro')
        setQueryError(t.code ? `${t.code} — ${t.friendly}` : t.friendly)
      }
    } catch (e) { setQueryError(translateRequestError(e)) }
    finally { executingSQLRef.current = false; setIsExecuting(false) }
    return null
  }, [authenticatedFetch, computeBrainStatus, activeHistoryId])

  // Traz o SQL de uma bolha do chat para o painel central. Antes disto não havia
  // caminho do chat para o centro: numa conversa de cinco perguntas só o SQL do
  // último turno existia no card, e os anteriores só dava para ler dentro da
  // coluna estreita. Não executa — mesma regra do openHistoryItem.
  const abrirSqlNoCentro = useCallback((sql: string) => {
    // Desvincula do item de histórico aberto: este SQL não é aquela linha, e
    // deixar o vínculo faria um clique em Executar atualizar a linha errada
    // (ver o caminho de reexecução por historyId no executeSQL).
    setActiveHistoryId(null)
    activeHistorySqlRef.current = ''
    setCurrentSQL(sql)
    setQueryResult(null); setQueryError(null); setReportView(false); setReportData(null); setSortCol(null)
    if (estreito) setFolhaAberta(true)
  }, [estreito])

  const copiarSqlDaBolha = useCallback((msgId: string, sql: string) => {
    navigator.clipboard.writeText(sql).then(() => {
      setCopiedMsgId(msgId)
      setTimeout(() => setCopiedMsgId(c => (c === msgId ? null : c)), 1500)
    }).catch(() => { /* clipboard bloqueado pelo browser */ })
  }, [])

  // Abre um item da árvore no centro SEM reexecutar no Oracle (decisão de design:
  // o meio mostra o SQL + a conversa; o Oracle só é consultado quando o usuário
  // clica em "Executar"). Carrega a conversa da geração e destaca o item na árvore.
  // `doUsuario` separa o TOQUE na árvore da restauração automática da última
  // consulta, que roda no boot. Sem essa distinção, em tela estreita o painel
  // abria já com a folha do resultado por cima de tudo — o oposto de cair na
  // conversa, que é onde se começa a trabalhar.
  const openHistoryItem = useCallback(async (item: QueryHistoryItem, doUsuario = false) => {
    setActiveHistoryId(item.id)
    // Escolher no histórico é o fim do propósito da gaveta: deixá-la aberta por
    // cima da conversa que acabou de ser carregada esconderia o resultado.
    setHistDrawer(false)
    // E abre a folha: em tela estreita o SQL e o botão Executar moram na coluna
    // central, que ali está escondida. Sem isto o toque no histórico não produzia
    // nada visível — o painel parecia ter ignorado o clique.
    if (doUsuario && estreitoRef.current) setFolhaAberta(true)
    // Guarda o SQL original do item: é a chave para saber, no executeSQL, se um
    // "Executar" é reexecução deste item (atualiza a linha) ou SQL novo (insere).
    activeHistorySqlRef.current = item.query_text || ''
    setCurrentSQL(item.query_text || '')
    setQueryResult(null); setQueryError(null); setReportView(false); setReportData(null); setSortCol(null)
    setActiveMeta({
      prompt: item.user_prompt || null,
      authorName: authorDisplayName(item),
      createdAt: item.created_at || null,
      status: item.status,
    })
    if (item.has_conversation || item.query_text) {
      try {
        const r = await authenticatedFetch(`/api/oracle/query-history/${item.id}/conversation`)
        const d = await r.json()
        if (d.success && d.messages?.length > 0) {
          // Chat multi-participativo: as bolhas 'user' levam nome/avatar/cor de quem
          // gerou (admin humano OU Clara). 'assistant' fica como "Assistente".
          const ownerName = authorDisplayName(item)
          const ownerId = item.user_id || null
          const ownerAvatar = isClaraItem(item) ? null : (item.avatar_url || null)
          const baseTs = item.created_at ? new Date(item.created_at) : new Date()
          setAiMessages(d.messages.map((m: { role: AiMessage['role']; content: string; sql?: string }) => ({
            id: crypto.randomUUID(),
            role: m.role,
            content: m.content,
            sql: m.sql,
            timestamp: baseTs,
            authorId: m.role === 'user' && ownerId ? ownerId : undefined,
            authorName: m.role === 'user' && ownerName ? ownerName : undefined,
            authorAvatar: m.role === 'user' && ownerAvatar ? ownerAvatar : undefined,
          })))
          setChatOpen(true)
        }
      } catch { /* sem conversa — fica só o SQL no card */ }
    }
  }, [authenticatedFetch])

  // Carrega o histórico GLOBAL (scope=all) — todos os admins + Clara. Não-admin
  // recebe só o próprio (backend decide). É a fonte da árvore lateral.
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true)
    try {
      const r = await authenticatedFetch(`${API_ENDPOINTS.ORACLE_QUERY_HISTORY}?scope=all&limit=150`)
      const d = await r.json()
      if (d.success) setQueryHistory(d.history)
    } catch {}
    finally { setHistoryLoading(false) }
  }, [authenticatedFetch])

  // Atualização viva: carrega no mount e refaz a cada 15s enquanto o painel
  // está aberto — novas gerações de qualquer admin ou da Clara aparecem sozinhas.
  useEffect(() => {
    loadHistory()
    const id = setInterval(loadHistory, 15000)
    return () => clearInterval(id)
  }, [loadHistory])

  // Centro nunca vazio: ao chegar o 1º histórico, carrega a última geração no
  // centro (SQL + conversa, sem reexecutar). Só 1x e só se o usuário ainda não
  // começou a gerar algo nesta sessão.
  //
  // A ÚLTIMA DO PRÓPRIO USUÁRIO, não a mais recente do mundo. Como queryHistory
  // vem com scope=all (feed de todos os admins), o antigo queryHistory[0] abria
  // a conversa de quem tivesse consultado por último — e a próxima pergunta ia
  // ao modelo como continuação dela, além de copiar a conversa alheia para
  // dentro da linha de histórico deste usuário. Abrir conversa de outra pessoa
  // continua possível, mas por clique deliberado na árvore lateral.
  useEffect(() => {
    if (didAutoLoadHistory.current || queryHistory.length === 0) return
    didAutoLoadHistory.current = true
    const minhaUltima = queryHistory.find(it => it.user_id === user?.id)
    if (!currentSQL && minhaUltima) openHistoryItem(minhaUltima)
  }, [queryHistory, currentSQL, openHistoryItem, user?.id])

  // As últimas perguntas DESTE usuário, para o chat vazio. Sai de queryHistory,
  // que já está carregado — nenhuma chamada nova. Substituiu uma lista de seis
  // exemplos escritos à mão que ninguém sabia se eram usados (não havia
  // telemetria no clique); estas são as consultas que a pessoa de fato faz.
  // A deduplicação por texto importa: refazer a mesma pergunta em turnos
  // seguidos é comum e encheria a lista com repetição.
  const minhasRecentes = useMemo(() => {
    const vistos = new Set<string>()
    const out: QueryHistoryItem[] = []
    for (const it of queryHistory) {
      if (it.user_id !== user?.id) continue
      const p = (it.user_prompt || '').trim()
      if (!p) continue
      const k = p.toLowerCase()
      if (vistos.has(k)) continue
      vistos.add(k); out.push(it)
      if (out.length >= 4) break
    }
    return out
  }, [queryHistory, user?.id])

  // Monta a árvore autor → período a partir do histórico plano. Filtro casa em
  // prompt/SQL/label/autor. Autores ordenados pela atividade mais recente (quem
  // gerou por último sobe), com períodos ordenados (Hoje → Mais antigo) dentro.
  const historyTree = useMemo(() => {
    const q = histFilter.trim().toLowerCase()
    const items = q
      ? queryHistory.filter(it =>
          (it.user_prompt || '').toLowerCase().includes(q) ||
          (it.query_text || '').toLowerCase().includes(q) ||
          (it.label || '').toLowerCase().includes(q) ||
          authorDisplayName(it).toLowerCase().includes(q))
      : queryHistory
    type Period = { key: string; label: string; order: number; items: QueryHistoryItem[] }
    type Author = { authorKey: string; name: string; avatar: string | null; isClara: boolean; isOwn: boolean; lastTs: number; count: number; periodsMap: Map<string, Period> }
    const byAuthor = new Map<string, Author>()
    for (const it of items) {
      const key = it.user_id || it.username || 'desconhecido'
      let a = byAuthor.get(key)
      if (!a) {
        a = {
          authorKey: key,
          name: authorDisplayName(it),
          avatar: isClaraItem(it) ? null : (it.avatar_url || null),
          isClara: isClaraItem(it),
          isOwn: !!user?.id && it.user_id === user.id,
          lastTs: 0, count: 0, periodsMap: new Map(),
        }
        byAuthor.set(key, a)
      }
      a.count++
      const ts = it.created_at ? new Date(it.created_at).getTime() : 0
      if (ts > a.lastTs) a.lastTs = ts
      const pb = periodBucket(it.created_at)
      let p = a.periodsMap.get(pb.key)
      if (!p) { p = { key: pb.key, label: pb.label, order: pb.order, items: [] }; a.periodsMap.set(pb.key, p) }
      p.items.push(it)
    }
    return [...byAuthor.values()]
      .sort((x, y) => y.lastTs - x.lastTs)
      .map(a => ({
        authorKey: a.authorKey, name: a.name, avatar: a.avatar,
        isClara: a.isClara, isOwn: a.isOwn, count: a.count,
        periods: [...a.periodsMap.values()].sort((p1, p2) => p1.order - p2.order),
      }))
  }, [queryHistory, histFilter, user?.id])

  const generateReport = useCallback(async () => {
    if (!queryResult?.rows?.length) return
    setReportLoading(true); setReportView(true)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.ORACLE_AI_REPORT, {
        method: 'POST',
        body: JSON.stringify({ columns: queryResult.columns, rows: queryResult.rows, rowCount: queryResult.rowCount, sql: currentSQL, historyId: queryResult.historyId }),
        timeout: 600000
      } as RequestInit)
      const d = await r.json()
      if (d.success) setReportData(d.report)
      else { setReportData(null); setReportView(false) }
    } catch { setReportData(null); setReportView(false) }
    finally { setReportLoading(false) }
  }, [queryResult, currentSQL, authenticatedFetch])

  // ─── Relatório PDF de celular (job de 2º plano) ───
  // O backend reusa o núcleo do /ai-report para KPIs e gráficos, pede o texto
  // interpretativo ao modelo, confere cada número contra a evidência apurada no
  // servidor e monta o PDF no mesmo pipeline dos relatórios do Telegram.
  // A geração leva 10-40s e NÃO vive mais dentro do request: quem faz o trabalho
  // é um job do servidor. Trocar de painel, dar F5 ou fechar a aba não cancela
  // nada — ao voltar, este componente se re-anexa pelo status do job.
  type RelJob = {
    jobId: string
    status: 'running' | 'done' | 'error'
    stage?: string | null
    stageLabel?: string | null
    progress?: number
    titulo?: string | null
    filename?: string | null
    savedAs?: string | null
    saveError?: string | null
    avisos?: { valoresNaoConfirmados?: number; analiseIndisponivel?: boolean; linhasNaTabela?: number; linhasTotais?: number } | null
    error?: string | null
    idadeSeg?: number
    idadeMin?: number | null
    jaExiste?: boolean
  }
  const [pdfJob, setPdfJob] = useState<RelJob | null>(null)
  const [pdfSegundos, setPdfSegundos] = useState(0)
  const [pdfError, setPdfError] = useState<string | null>(null)
  const [pdfAviso, setPdfAviso] = useState<string | null>(null)
  const [pdfBaixando, setPdfBaixando] = useState(false)
  // Jobs que ESTA aba pediu: só eles baixam sozinhos ao ficar prontos. Quem
  // abriu o painel no meio do caminho ganha o botão, não um download surpresa.
  const pdfAutoRef = useRef<Set<string>>(new Set())
  const pdfLoading = pdfJob?.status === 'running'

  const baixarRelatorio = useCallback(async (job: RelJob | null) => {
    if (!job?.jobId) return
    setPdfBaixando(true)
    try {
      const r = await authenticatedFetch(`${API_ENDPOINTS.ORACLE_REPORT_JOBS}/${job.jobId}/pdf`, { timeout: 120000 } as RequestInit)
      if (!r.ok) throw new Error('O arquivo não está mais disponível — gere o relatório de novo.')
      const blob = await r.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = job.filename || 'relatorio.pdf'; a.click()
      window.setTimeout(() => URL.revokeObjectURL(url), 4000)
    } catch (e) {
      setPdfError(e instanceof Error ? e.message : 'Falha ao baixar o relatório')
      window.setTimeout(() => setPdfError(null), 8000)
    } finally {
      setPdfBaixando(false)
    }
  }, [authenticatedFetch])

  // Dispensar = "já vi". O servidor apaga o PDF guardado e para de oferecer.
  const dispensarRelatorio = useCallback(async (jobId?: string) => {
    const id = jobId || pdfJob?.jobId
    setPdfJob(null); setPdfAviso(null)
    if (!id) return
    try { await authenticatedFetch(`${API_ENDPOINTS.ORACLE_REPORT_JOBS}/${id}/ack`, { method: 'POST' }) } catch { /* some sozinho no TTL */ }
  }, [pdfJob, authenticatedFetch])

  const aplicarJob = useCallback((j: RelJob) => {
    setPdfJob(j)
    if (j.status === 'error') {
      setPdfError(j.error || 'Falha ao gerar o relatório')
      return
    }
    if (j.status === 'done') {
      const partes: string[] = []
      if (j.savedAs) partes.push('Arquivado no Storage de documentos.')
      else if (j.saveError) partes.push(j.saveError)
      if (j.avisos?.analiseIndisponivel) partes.push('a análise automática ficou indisponível — os números vieram direto do banco')
      if ((j.avisos?.valoresNaoConfirmados || 0) > 0) partes.push(`${j.avisos!.valoresNaoConfirmados} valor(es) do texto não conferiam e foram removidos`)
      setPdfAviso(partes.length ? partes.join(' · ') : null)
      if (pdfAutoRef.current.has(j.jobId)) {
        pdfAutoRef.current.delete(j.jobId)
        baixarRelatorio(j)
      }
    }
  }, [baixarRelatorio])

  // Re-anexação: ao montar o painel, pergunta ao servidor se há relatório em
  // andamento (ou pronto e ainda não dispensado) deste usuário.
  useEffect(() => {
    let vivo = true
    ;(async () => {
      try {
        const r = await authenticatedFetch(API_ENDPOINTS.ORACLE_REPORT_JOBS)
        if (!r.ok) return
        const d = await r.json()
        if (!vivo || !Array.isArray(d?.jobs) || !d.jobs.length) return
        const j: RelJob = d.jobs.find((x: RelJob) => x.status === 'running') || d.jobs[0]
        if (j) aplicarJob(j)
      } catch { /* painel funciona sem isto */ }
    })()
    return () => { vivo = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Enquanto roda: pergunta o status a cada 2,5s.
  useEffect(() => {
    if (pdfJob?.status !== 'running') return
    const id = pdfJob.jobId
    let vivo = true
    const t = window.setInterval(async () => {
      try {
        const r = await authenticatedFetch(`${API_ENDPOINTS.ORACLE_REPORT_JOBS}/${id}`)
        if (!r.ok || !vivo) return
        const d = await r.json()
        if (!vivo || !d?.jobId) return
        if (d.status !== 'running') aplicarJob(d)
        else setPdfJob(prev => (prev && prev.jobId === d.jobId ? { ...prev, ...d } : prev))
      } catch { /* rede oscilou; a próxima volta resolve */ }
    }, 2500)
    return () => { vivo = false; window.clearInterval(t) }
  }, [pdfJob?.jobId, pdfJob?.status, authenticatedFetch, aplicarJob])

  // Relógio: a base vem do servidor (idadeSeg), então o contador continua certo
  // depois de um F5 em vez de recomeçar do zero.
  useEffect(() => {
    if (pdfJob?.status !== 'running') return
    const t0 = Date.now() - (pdfJob.idadeSeg || 0) * 1000
    const tick = () => setPdfSegundos(Math.max(0, Math.round((Date.now() - t0) / 1000)))
    tick()
    const t = window.setInterval(tick, 1000)
    return () => window.clearInterval(t)
  }, [pdfJob?.jobId, pdfJob?.status, pdfJob?.idadeSeg])

  const exportReportPDF = useCallback(async (forcar = false) => {
    if (!queryResult?.rows?.length || pdfLoading) return
    // Só o erro é encerrado — senão ele voltaria a aparecer a cada montagem do
    // painel. Um relatório PRONTO continua guardado: é o que permite devolvê-lo
    // na hora em vez de gastar outros 30s gerando o mesmo PDF.
    if (pdfJob?.status === 'error') dispensarRelatorio(pdfJob.jobId)
    setPdfError(null); setPdfAviso(null); setPdfSegundos(0)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.ORACLE_REPORT_PDF, {
        method: 'POST',
        body: JSON.stringify({
          columns: queryResult.columns,
          rows: queryResult.rows,
          rowCount: queryResult.rowCount,
          maxRowsReached: queryResult.maxRowsReached,
          sql: currentSQL,
          pergunta: aiMessages.filter(m => m.role === 'user').pop()?.content || '',
          historyId: queryResult.historyId,
          origem: 'planalto',
          forcar,
        }),
        timeout: 120000
      } as RequestInit)
      const d = await r.json()
      if (!r.ok || !d.ok || !d.jobId) throw new Error(d.error || 'Falha ao iniciar o relatório')
      // Mesma consulta, relatório ainda guardado: volta pronto na hora. O
      // usuário clicou querendo o arquivo, então baixa igual.
      if (d.jaExiste) {
        pdfAutoRef.current.add(d.jobId)
        aplicarJob({ ...d, jaExiste: true })
        return
      }
      if (d.jaRodando) {
        setPdfAviso('Já havia um relatório sendo gerado — acompanhando esse.')
        window.setTimeout(() => setPdfAviso(null), 8000)
      } else {
        pdfAutoRef.current.add(d.jobId)
      }
      setPdfJob({ jobId: d.jobId, status: d.status || 'running', stageLabel: d.stageLabel || 'Na fila…', progress: d.progress || 2, idadeSeg: d.idadeSeg || 0 })
    } catch (e) {
      console.error('[Relatório PDF] erro', e)
      setPdfError(e instanceof Error ? e.message : 'Falha ao iniciar o relatório')
      window.setTimeout(() => setPdfError(null), 8000)
    }
  }, [queryResult, currentSQL, aiMessages, pdfLoading, pdfJob, dispensarRelatorio, aplicarJob, authenticatedFetch])

  // Estado do cartão: um erro que impediu o job de nascer também precisa
  // aparecer, e nesse caso não existe pdfJob para consultar.
  const pdfCard: 'running' | 'done' | 'error' | null = pdfJob?.status || (pdfError ? 'error' : null)
  const pdfQuando = (min?: number | null) =>
    min == null ? '' : min < 1 ? 'agora há pouco' : min < 60 ? `há ${min} min` : `há ${Math.floor(min / 60)}h`


  const loadCachedReport = useCallback(async (historyId: number) => {
    setReportLoading(true); setReportView(true)
    try {
      const r = await authenticatedFetch(`/api/oracle/query-history/${historyId}/report`)
      const d = await r.json()
      if (d.success) setReportData(d.report)
      else { setReportData(null); setReportView(false) }
    } catch { setReportData(null); setReportView(false) }
    finally { setReportLoading(false) }
  }, [authenticatedFetch])

  const deleteHistoryItem = useCallback(async (id: number) => {
    try {
      await authenticatedFetch(`/api/oracle/query-history/${id}`, { method: 'DELETE' })
      setQueryHistory(p => p.filter(h => h.id !== id))
    } catch {}
  }, [authenticatedFetch])

  const renameHistoryItem = useCallback(async (id: number, label: string) => {
    try {
      await authenticatedFetch(`/api/oracle/query-history/${id}`, { method: 'PATCH', body: JSON.stringify({ label }) })
      setQueryHistory(p => p.map(h => h.id === id ? { ...h, label } : h))
    } catch {}
  }, [authenticatedFetch])

  const exportCSV = useCallback(() => {
    if (!queryResult?.rows?.length) return
    const h = queryResult.columns.map(c => c.name)
    const csv = [h.join(','), ...queryResult.rows.map(row => h.map(k => { const v = row[k]; if (v == null) return ''; const s = String(v); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s }).join(','))].join('\n')
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' }))
    a.download = `query_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`; a.click()
  }, [queryResult])

  const exportTXT = useCallback(() => {
    if (!queryResult?.rows?.length) return
    const h = queryResult.columns.map(c => c.name)
    const txt = [h.join('\t'), ...queryResult.rows.map(row => h.map(k => String(row[k] ?? '')).join('\t'))].join('\n')
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([txt], { type: 'text/plain;charset=utf-8;' }))
    a.download = `query_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`; a.click()
  }, [queryResult])

  const [exportXlsxError, setExportXlsxError] = useState<string | null>(null)
  const exportXLSX = useCallback(async () => {
    if (!queryResult?.rows?.length) return
    try {
      const XLSX = await import('xlsx')
      const cols = queryResult.columns.map(c => c.name)
      const data = queryResult.rows.map(row => {
        const obj: Record<string, unknown> = {}
        cols.forEach(c => { obj[c] = row[c] ?? '' })
        return obj
      })
      const ws = XLSX.utils.json_to_sheet(data)
      ws['!cols'] = cols.map(c => {
        const maxLen = Math.max(c.length, ...data.map(r => String(r[c] ?? '').length))
        return { wch: Math.min(maxLen + 2, 50) }
      })
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Resultado')
      XLSX.writeFile(wb, `query_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`)
    } catch (e) {
      // Falha pode ser: módulo xlsx não carregou (lazy import bloqueado, rede),
      // ou writeFile sem permissão de download. Mostra erro inline 5s.
      console.error('[Export XLSX] erro', e)
      setExportXlsxError(e instanceof Error ? e.message : 'Falha ao exportar')
      window.setTimeout(() => setExportXlsxError(null), 5000)
    }
  }, [queryResult])

  const copyResults = useCallback(() => {
    if (!queryResult?.rows?.length) return
    const h = queryResult.columns.map(c => c.name)
    navigator.clipboard.writeText([h.join('\t'), ...queryResult.rows.map(row => h.map(k => String(row[k] ?? '')).join('\t'))].join('\n'))
  }, [queryResult])

  // ─── AI (Camada 8: SSE streaming) ───
  // Backend devolve text/event-stream quando o cliente envia Accept: text/event-stream.
  // Eventos: status (etapas do pipeline), thinking (chunk do raciocínio do modelo),
  // content (chunk do JSON de resposta — só pra debug, descartado aqui),
  // validation (resultado de cada tentativa), retry_start (modelo vai corrigir),
  // done (payload final igual ao JSON antigo), error (falha).
  // textOverride: usado pela entrada por voz (transcrição chega pronta, sem
  // depender do estado aiInput). typeof-check porque onClick passa o evento.
  const generateSqlFromAi = useCallback(async (textOverride?: unknown) => {
    const promptText = (typeof textOverride === 'string' ? textOverride : aiInput).trim()
    if (!promptText || aiLoading) return
    // Aborta qualquer stream anterior em flight (paranoia — aiLoading
    // já bloqueia disparo duplo via UI, mas se algo escapar, garante).
    if (aiAbortCtrlRef.current) {
      try { aiAbortCtrlRef.current.abort(new DOMException('Nova requisição', 'AbortError')) } catch { /* */ }
    }
    const ctrl = new AbortController()
    aiAbortCtrlRef.current = ctrl
    const myRequestId = ++aiRequestIdRef.current

    const userMsg: AiMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: promptText,
      timestamp: new Date(),
      authorId: user?.id,
      authorName: user?.fullName || user?.username || 'Você',
      authorAvatar: null,
    }
    setAiMessages(p => [...p, userMsg]); setAiInput(''); setAiLoading(true)
    setStreamingThinking(''); setThinkingOpen(true)
    setInjectedContext({ vaultTables: [], learnedSlugs: [] })
    setAiElapsedMs(0)
    // Reset zero-row warning state — sem isso, a UI mostra "A IA não trouxe
    // dados" da query anterior se o usuário rodar uma query manual depois.
    setLastAiZeroRowFinal(false)
    setLastAiZeroRowAttempts(0)

    // Debounce de setStreamingThinking via requestAnimationFrame:
    // SSE pode chegar 200+ chunks/request, cada um disparando setState =>
    // re-render. Coalescemos atualizações em 1 por frame (~60fps) sem perder
    // bytes. Pendente flush no final do request pra garantir conteúdo completo.
    let thinkingPending = ''
    let thinkingRaf = 0
    const flushThinking = () => {
      thinkingRaf = 0
      if (myRequestId !== aiRequestIdRef.current) return
      setStreamingThinking(thinkingPending)
    }

    setAiStage({ step: 1, label: 'Conectando ao Oracle...' })

    // Cronômetro local: tick a cada 1s pra UI mostrar progresso visual.
    // Heartbeat do backend (a cada 25s) sincroniza opcionalmente caso o
    // clock local divirja.
    const startedAt = Date.now()
    const tickerId = window.setInterval(() => {
      if (myRequestId !== aiRequestIdRef.current) { window.clearInterval(tickerId); return }
      setAiElapsedMs(Date.now() - startedAt)
    }, 1000)

    try {
      // O turno do assistente vai como SQL + o RESULTADO que ele produziu.
      // Antes ia só o SQL: o modelo sabia o que perguntou e não o que respondeu,
      // então um "quem foram" não tinha âncora e um "você disse 12" era
      // irrespondível. O bloco tem teto de 400 chars (ver formatTurnResult).
      // Cinto de segurança: só turnos DESTE usuário viram contexto. O chat é
      // multi-participativo de leitura — clicar num item do Ricardo mostra a
      // conversa dele —, mas mandar os turnos alheios ao modelo fazia a pergunta
      // nova virar continuação da conversa de outra pessoa. `!m.authorId` cobre
      // os turnos da sessão corrente, que nascem sem autor carimbado.
      const hist = aiMessages
        .filter(m => m.role !== 'error' && m.role !== 'system')
        .filter(m => m.role !== 'user' || !m.authorId || m.authorId === user?.id)
        .slice(-6).map(m => ({
          role: m.role,
          content: m.role === 'assistant'
            ? (m.sql ? m.sql + formatTurnResult(m.result) : m.content)
            : m.content,
        }))

      const r = await authenticatedFetch(API_ENDPOINTS.ORACLE_AI_GENERATE, {
        method: 'POST',
        headers: { 'Accept': 'text/event-stream' },
        body: JSON.stringify({ prompt: userMsg.content, conversationHistory: hist, provider: 'claude' }),
        timeout: 600000,
        signal: ctrl.signal,
      } as RequestInit)

      if (!r.body) throw new Error('Stream indisponível')

      // ─── Parser SSE manual ───
      // Cada evento é "event: NAME\ndata: JSON\n\n". A leitura pode partir
      // um evento ao meio entre chunks; mantemos um buffer e processamos
      // só blocos completos terminados em \n\n.
      const reader = r.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let finalPayload: Record<string, unknown> | null = null
      let finalError: string | null = null
      let thinkingAcc = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        // Guarda contra race: se outra request foi disparada, descarta tudo
        // que chegar deste reader. Ele será garbage-collected após o break.
        if (myRequestId !== aiRequestIdRef.current) break
        buffer += decoder.decode(value, { stream: true })
        let sepIdx
        while ((sepIdx = buffer.indexOf('\n\n')) >= 0) {
          const rawEvent = buffer.slice(0, sepIdx)
          buffer = buffer.slice(sepIdx + 2)
          let eventName = 'message'
          let dataLine = ''
          for (const line of rawEvent.split('\n')) {
            if (line.startsWith('event:')) eventName = line.slice(6).trim()
            else if (line.startsWith('data:')) dataLine += line.slice(5).trim()
          }
          if (!dataLine) continue
          let payload: Record<string, unknown>
          try { payload = JSON.parse(dataLine) } catch { continue }

          // Todo evento carrega _seq: é o cursor do replay. Guardar sempre, para
          // que um re-attach depois de trocar de painel não repita o que já foi visto.
          if (typeof payload._seq === 'number') aiLastSeqRef.current = payload._seq as number

          if (eventName === 'job') {
            // Chega ANTES de tudo, de propósito: se o usuário der F5 no segundo
            // seguinte, a referência do job já está gravada e o painel reencontra
            // o trabalho em vez de perdê-lo.
            const jid = String((payload as { jobId?: string }).jobId || '')
            if (jid) {
              aiJobIdRef.current = jid
              if (user?.id) salvaJobRef(user.id, { jobId: jid, startedAt: Date.now(), prompt: promptText, lastSeq: 0 })
            }
          } else if (eventName === 'model') {
            const p = payload as { model?: string; tier?: number }
            if (typeof p.tier === 'number') setAiTier(p.tier)
          } else if (eventName === 'tier') {
            const p = payload as { tier?: number; label?: string; round?: number; of?: number }
            setAiTier(p.tier || 0)
            setAiStage({ step: 2, label: `Rodada ultra ${p.round || 1}/${p.of || 2} — ${p.label || ''}` })
          } else if (eventName === 'escalate_offer') {
            setEscalateOffer(payload as unknown as OfertaEscalonamento)
          } else if (eventName === 'status') {
            const stage = String(payload.stage || '')
            const label = String(payload.message || '')
            const stageMap: Record<string, number> = {
              init: 1, generating: 2, parsing: 3,
              validating: 4, correcting: 5, testing: 6, complete: 7
            }
            setAiStage({ step: stageMap[stage] || 0, label })
          } else if (eventName === 'context') {
            const p = payload as { vault_tables?: string[]; learned_examples?: string[] }
            setInjectedContext({
              vaultTables: p.vault_tables || [],
              learnedSlugs: p.learned_examples || []
            })
          } else if (eventName === 'thinking') {
            const text = String((payload as { text?: string }).text || '')
            thinkingAcc += text
            thinkingPending = thinkingAcc
            if (!thinkingRaf) thinkingRaf = window.requestAnimationFrame(flushThinking)
          } else if (eventName === 'retry_start') {
            // Inserir separador no thinking pra demarcar onde o modelo "recomeçou"
            // zero-rows não é erro de sintaxe: o SQL rodou e voltou vazio. Dizer
            // "ajustando até o Oracle aceitar" aqui confunde — o Oracle aceitou.
            const motivo = payload.source === 'lint' ? 'polindo a sintaxe Oracle 11g'
              : payload.source === 'zero-rows' ? 'procurando onde os dados realmente estão'
              : 'ajustando até o Oracle aceitar'
            thinkingAcc += `\n\n── ${motivo}, quase lá ──\n\n`
            thinkingPending = thinkingAcc
            if (!thinkingRaf) thinkingRaf = window.requestAnimationFrame(flushThinking)
          } else if (eventName === 'validation') {
            // só log no console — etapa já vem por 'status'
            console.log('[Oracle AI SSE] validation', payload)
          } else if (eventName === 'heartbeat') {
            // Sincronizar com tempo do servidor se desviar > 2s
            const srv = Number((payload as { elapsedMs?: number }).elapsedMs || 0)
            if (srv > 0 && Math.abs(srv - (Date.now() - startedAt)) > 2000) {
              setAiElapsedMs(srv)
            }
          } else if (eventName === 'done') {
            finalPayload = payload
          } else if (eventName === 'error') {
            // Backend emite {code, error, oracleError, sql, raw}. Prioridade:
            // 1. err.error (backend manda contextual amigável — "Tabela não
            //    encontrada. A IA usou um nome que não existe — refine...")
            // 2. translateOracleError(oracleError) como fallback se o backend
            //    deixou error vazio mas o oracleError contém um ORA-xxxxx
            // 3. "Erro desconhecido" como último recurso
            // Trocar o item 1 pelo 2 perde contexto rico (a IA está alucinando,
            // refine a pergunta) por uma mensagem genérica. Evita-se.
            const err = payload as { code?: string; error?: string; oracleError?: string; raw?: string; sql?: string }
            if (err.code || err.raw) {
              console.warn('[Oracle AI SSE] error', err.code, err.oracleError || err.raw)
            }
            if (err.error && err.error.trim()) {
              finalError = err.error
            } else if (err.oracleError) {
              finalError = translateOracleError(err.oracleError).friendly
            } else {
              finalError = 'Erro desconhecido'
            }
          }
          // content event não é renderizado (é o JSON cru — usuário vê o SQL formatado depois)
        }
      }

      if (finalError) throw new Error(finalError)
      if (!finalPayload) throw new Error('Stream terminou sem evento done')

      const d = finalPayload as {
        conversation?: boolean; success?: boolean; sql?: string; error?: string;
        message?: string; suggestions?: string[]; explanation?: string;
        modelUsed?: string; validatedQueryId?: number | null;
        zeroRowsFinal?: boolean; zeroRowAttempts?: number;
      }

      // Camada 5: capturar flags de zero-rows pra UI exibir aviso específico
      setLastAiZeroRowFinal(!!d.zeroRowsFinal)
      setLastAiZeroRowAttempts(d.zeroRowAttempts || 0)

      if (d.conversation) {
        const suggestionText = d.suggestions && d.suggestions.length > 0
          ? '\n\n**Sugestões:**\n' + d.suggestions.map((s: string) => `- ${s}`).join('\n')
          : ''
        setAiMessages(p => [...p, {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: (d.message || '') + suggestionText,
          timestamp: new Date(),
          thinking: thinkingAcc || undefined
        }])
      } else if (d.success && d.sql) {
        const assistantMsg: AiMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: d.explanation || 'SQL gerado.',
          sql: d.sql,
          timestamp: new Date(),
          validatedQueryId: d.validatedQueryId ?? null,
          userVote: null,
          thinking: thinkingAcc || undefined
        }
        setAiMessages(p => [...p, assistantMsg])
        setThinkingOpen(false)  // colapsa após sucesso

        // conversation_json passa a guardar o rowCount de cada turno ANTERIOR
        // (o do turno corrente já vai na coluna row_count da mesma linha), para
        // que o corpus de conversas — scripts/corpus-conversas.cjs — melhore
        // sozinho a cada uso.
        const fullConv = [...aiMessages.filter(m => m.role !== 'error'), userMsg, assistantMsg]
        const convJson = JSON.stringify(fullConv.map(m => ({ role: m.role, content: m.content, sql: m.sql, rowCount: m.result?.rowCount })))
        const turnResult = await executeSQL(d.sql, userMsg.content, { conversationJson: convJson, aiExplanation: d.explanation })
        // Anexa o resultado à bolha que acabou de ser empilhada, por id. É o que
        // o PRÓXIMO turno vai ler no histórico.
        if (turnResult) {
          setAiMessages(p => p.map(m => (m.id === assistantMsg.id ? { ...m, result: turnResult } : m)))
        }
      } else {
        setAiMessages(p => [...p, { id: crypto.randomUUID(), role: 'error', content: d.error || 'Erro', sql: d.sql, timestamp: new Date(), thinking: thinkingAcc || undefined }])
      }
    } catch (e) {
      // Cancelamento explícito (usuário clicou cancelar ou nova request) — silencioso
      const isAbort = e instanceof DOMException && e.name === 'AbortError'
      // Descarta resultado tardio se outra request já assumiu
      if (myRequestId !== aiRequestIdRef.current) { return }
      if (!isAbort) {
        setAiMessages(p => [...p, { id: crypto.randomUUID(), role: 'error', content: translateRequestError(e), timestamp: new Date() }])
      }
    }
    finally {
      window.clearInterval(tickerId)
      if (thinkingRaf) { window.cancelAnimationFrame(thinkingRaf); thinkingRaf = 0 }
      // Só limpa estado se este request ainda é o atual (não foi superado)
      if (myRequestId === aiRequestIdRef.current) {
        setAiLoading(false); setAiStage(null); setStreamingThinking(''); setAiElapsedMs(0); userScrolledUp.current = false
        aiAbortCtrlRef.current = null
      }
    }
  }, [aiInput, aiLoading, aiMessages, authenticatedFetch, executeSQL, user])

  // Cancelar agora para o TRABALHO no servidor, não só a escuta. Antes, abortar
  // o fetch só escondia a tela: o pipeline seguia até o fim queimando plano Max.
  const cancelAiGeneration = useCallback(() => {
    const jobId = aiJobIdRef.current
    if (jobId) { void cancelaJob(authenticatedFetch, jobId); aiJobIdRef.current = null }
    if (user?.id) salvaJobRef(user.id, null)
    if (aiAbortCtrlRef.current) {
      aiAbortCtrlRef.current.abort(new DOMException('Cancelado pelo usuário', 'AbortError'))
      aiAbortCtrlRef.current = null
    }
    setAiLoading(false); setAiStage(null); setStreamingThinking(''); setEscalateOffer(null)
    setAiMessages(p => [...p, {
      id: crypto.randomUUID(),
      role: 'system',
      content: 'Geração cancelada.',
      timestamp: new Date()
    }])
  }, [authenticatedFetch, user?.id])

  const handleAiKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Em tela estreita o Enter NÃO envia: no teclado virtual ele é a tecla de
    // quebra de linha, e não há Shift à mão para escapar dela. Quem envia é o
    // botão, que abaixo de 1280px está sempre visível justamente por isto.
    if (e.key === 'Enter' && !e.shiftKey && !estreito) { e.preventDefault(); generateSqlFromAi() }
    else if (e.key === 'Escape' && aiLoading) { e.preventDefault(); cancelAiGeneration() }
  }, [generateSqlFromAi, aiLoading, cancelAiGeneration, estreito])

  // A caixa cresce com o conteúdo abaixo de 1280px. Em xl ela continua travada
  // em rows={2}: lá a altura do rodapé do chat precisa casar com a do rodapé da
  // sidebar, e cada linha desloca a divisória em ~19px (ver o comentário no
  // JSX). Sem coluna ao lado, essa razão deixa de existir.
  useEffect(() => {
    const ta = aiInputRef.current
    if (!ta) return
    if (!estreito) { ta.style.removeProperty('height'); ta.style.removeProperty('overflow-y'); return }
    ta.style.height = 'auto'
    const TETO = 180 // ~6 linhas de 16px com leading-relaxed, mais o padding
    ta.style.height = Math.min(ta.scrollHeight, TETO) + 'px'
    ta.style.overflowY = ta.scrollHeight > TETO ? 'auto' : 'hidden'
  }, [aiInput, estreito])

  useEffect(() => { generateRef.current = generateSqlFromAi }, [generateSqlFromAi])
  useEffect(() => { aiMessagesRef.current = aiMessages }, [aiMessages])

  // Aplica o resultado de um job que terminou enquanto o painel estava fora do ar
  // (usuário em outro painel, ou pós-F5). É uma versão enxuta do pós-processamento
  // do generateSqlFromAi: sem thinking acumulado (que não sobreviveu ao unmount) e
  // sem o userMsg em mão — o prompt vem do próprio job.
  const aplicaResultadoJob = useCallback(async (d: Record<string, unknown>, promptOriginal: string) => {
    setLastAiZeroRowFinal(!!d.zeroRowsFinal)
    setLastAiZeroRowAttempts(Number(d.zeroRowAttempts || 0))
    if (d.conversation) {
      setAiMessages(p => [...p, {
        id: crypto.randomUUID(), role: 'assistant',
        content: String(d.message || ''), timestamp: new Date(),
      }])
      return
    }
    if (d.success && d.sql) {
      const assistantMsg: AiMessage = {
        id: crypto.randomUUID(), role: 'assistant',
        content: String(d.explanation || 'SQL gerado.'),
        sql: String(d.sql), timestamp: new Date(),
        validatedQueryId: (d.validatedQueryId as number) ?? null, userVote: null,
      }
      setAiMessages(p => [...p, assistantMsg])
      // conversation_json precisa ir aqui também. Sem ele, o turno recuperado de
      // um job em segundo plano virava uma linha ÓRFÃ no histórico — que, por ser
      // a mais recente, era aberta pelo auto-load e fazia o backend sintetizar
      // uma conversa de 2 mensagens que nunca existiu. Lido de uma ref para não
      // entrar nas deps deste callback: o effect de re-anexação depende dele, e
      // recriá-lo a cada mensagem re-dispararia o attach.
      const convJson = JSON.stringify(
        [...aiMessagesRef.current.filter(m => m.role !== 'error'), assistantMsg]
          .map(m => ({ role: m.role, content: m.content, sql: m.sql, rowCount: m.result?.rowCount }))
      )
      const turnResult = await executeSQL(String(d.sql), promptOriginal, {
        conversationJson: convJson, aiExplanation: String(d.explanation || ''),
      })
      if (turnResult) setAiMessages(p => p.map(m => (m.id === assistantMsg.id ? { ...m, result: turnResult } : m)))
    } else {
      setAiMessages(p => [...p, {
        id: crypto.randomUUID(), role: 'error',
        content: String(d.error || 'Erro'), timestamp: new Date(),
      }])
    }
  }, [executeSQL])

  // ─── Re-anexar ao job em andamento ao montar ───
  // É isto que faz a consulta sobreviver a trocar de painel no menu ou dar F5:
  // o trabalho continua no servidor e o painel volta a escutar de onde parou,
  // usando fromSeq para não reprocessar o que já tinha visto.
  useEffect(() => {
    if (!user?.id) return
    let cancelado = false
    ;(async () => {
      const ativos = await listaJobsAtivos(authenticatedFetch)
      const job = ativos[0]
      if (!job || cancelado) return
      aiJobIdRef.current = job.id
      const ref = carregaJobRef(user.id)
      const fromSeq = ref?.jobId === job.id ? ref.lastSeq : 0
      aiLastSeqRef.current = fromSeq
      setAiLoading(true)
      setAiTier(job.tier || 0)
      setAiStage({ step: 0, label: job.stage_label || 'Retomando consulta…' })
      // Cronômetro ancorado no started_at do SERVIDOR: o setInterval local é só
      // tick visual e congela quando a aba vai para segundo plano.
      const inicio = job.started_at ? new Date(job.started_at).getTime() : Date.now()
      setAiElapsedMs(Date.now() - inicio)
      const ticker = window.setInterval(() => setAiElapsedMs(Date.now() - inicio), 1000)
      try {
        const r = await anexaJob(authenticatedFetch, job.id, fromSeq)
        if (!r.body) return
        let final: Record<string, unknown> | null = null
        let erro: string | null = null
        await consumeSse(r.body, {
          aindaValido: () => !cancelado,
          onEvent: (ev, payload) => {
            if (typeof payload._seq === 'number') {
              aiLastSeqRef.current = payload._seq as number
              salvaJobRef(user.id, { jobId: job.id, startedAt: inicio, prompt: job.prompt || '', lastSeq: payload._seq as number })
            }
            if (ev === 'status') {
              const stageMap: Record<string, number> = { init: 1, generating: 2, parsing: 3, validating: 4, correcting: 5, testing: 6, complete: 7 }
              setAiStage({ step: stageMap[String(payload.stage || '')] || 0, label: String(payload.message || '') })
            } else if (ev === 'tier') {
              setAiTier(Number(payload.tier || 0))
            } else if (ev === 'escalate_offer') {
              setEscalateOffer(payload as unknown as OfertaEscalonamento)
            } else if (ev === 'done') { final = payload }
            else if (ev === 'error') { erro = String(payload.error || 'Erro') }
          },
        })
        if (cancelado) return
        if (final) await aplicaResultadoJob(final, job.prompt || '')
        else if (erro) setAiMessages(p => [...p, { id: crypto.randomUUID(), role: 'error', content: erro!, timestamp: new Date() }])
      } catch { /* rede caiu: o job segue no servidor, próxima montagem re-anexa */ }
      finally {
        window.clearInterval(ticker)
        if (!cancelado) { setAiLoading(false); setAiStage(null) }
        salvaJobRef(user.id, null)
        aiJobIdRef.current = null
      }
    })()
    // Só "para de escutar" — nunca cancela o job. Cancelar é ação explícita do
    // usuário (botão/Esc), que chama cancelaJob no servidor.
    return () => { cancelado = true }
  }, [user?.id, authenticatedFetch, aplicaResultadoJob])

  // Aceitar o modo ultra. O servidor revalida permissão, tier e cota do dia.
  const aceitarModoUltra = useCallback(async () => {
    if (!escalateOffer || escalando) return
    setEscalando(true)
    const r = await escalaJob(authenticatedFetch, escalateOffer.jobId)
    setEscalando(false)
    if (!r.ok) {
      setAiMessages(p => [...p, { id: crypto.randomUUID(), role: 'error', content: r.erro || 'Não foi possível escalar.', timestamp: new Date() }])
      setEscalateOffer(null)
      return
    }
    setEscalateOffer(null)
    setAiLoading(true)
    setAiTier(r.tier || 1)
    setAiStage({ step: 2, label: 'Reprocessando em modo ultra…' })
  }, [escalateOffer, escalando, authenticatedFetch])

  const transcribeAndSend = useCallback(async (file: File) => {
    setRecState('transcribing')
    try {
      const fd = new FormData()
      fd.append('audio', file)
      fd.append('language', 'pt')
      const r = await authenticatedFetch(API_ENDPOINTS.WHISPER_TRANSCRIBE, { method: 'POST', body: fd })
      const data = await r.json().catch(() => ({}))
      if (!r.ok || !data.ok) throw new Error(data.error || `HTTP ${r.status}`)
      const text = String(data.text || '').replace(/\s+/g, ' ').trim()
      if (!text) { setRecError('Não entendi o áudio — tente falar de novo.'); return }
      // O texto CAI NA CAIXA e espera o toque em enviar (decisão do dono,
      // 01/09/2026). Antes ele disparava sozinho: nome próprio ou número que o
      // Whisper errasse virava consulta errada sem chance de correção — e no
      // tablet, em salão barulhento, é onde ele mais erra. O foco vem junto para
      // o cursor já estar no fim do texto, pronto para o ajuste.
      setAiInput(text)
      requestAnimationFrame(() => {
        const ta = aiInputRef.current
        if (!ta) return
        ta.focus()
        ta.setSelectionRange(text.length, text.length)
      })
    } catch (e) {
      setRecError('Falha na transcrição: ' + (e instanceof Error ? e.message : 'erro'))
    } finally {
      setRecState('idle')
    }
  }, [authenticatedFetch])

  const startRecording = useCallback(async () => {
    if (recState !== 'idle' || aiLoading) return
    setRecError(null)
    if (!navigator.mediaDevices?.getUserMedia) {
      setRecError('Microfone requer acesso via HTTPS (crm.twms.com.br).')
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mr = new MediaRecorder(stream)
      recChunksRef.current = []
      mr.ondataavailable = e => { if (e.data.size) recChunksRef.current.push(e.data) }
      mr.onstop = () => {
        const type = mr.mimeType || 'audio/webm'
        const ext = type.includes('ogg') ? 'ogg' : type.includes('mp4') ? 'mp4' : 'webm'
        stream.getTracks().forEach(t => t.stop())
        transcribeAndSend(new File([new Blob(recChunksRef.current, { type })], `consulta.${ext}`, { type }))
      }
      mr.start()
      mediaRecorderRef.current = mr
      setRecState('recording'); setRecSecs(0)
      recTimerRef.current = setInterval(() => setRecSecs(s => s + 1), 1000)
    } catch (e) {
      setRecError('Microfone indisponível: ' + (e instanceof Error ? e.message : 'permissão negada'))
    }
  }, [recState, aiLoading, transcribeAndSend])

  const stopRecording = useCallback(() => {
    if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null }
    mediaRecorderRef.current?.stop()
  }, [])

  // solta microfone/timer se o painel desmontar no meio de uma gravação
  useEffect(() => () => {
    if (recTimerRef.current) clearInterval(recTimerRef.current)
    try { mediaRecorderRef.current?.stream?.getTracks().forEach(t => t.stop()) } catch { /* */ }
  }, [])

  // Limpa chat + coluna central de uma vez. Era um arrow inline na vassoura do
  // header; virou nome próprio porque o menu ⋯ da barra de topo (tablet) precisa
  // da MESMA ação — duas cópias divergiriam no primeiro estado novo.
  const limpaTudo = useCallback(() => {
    // Invalida qualquer stream em voo: callbacks pendentes veem myRequestId desatualizado e desistem
    aiRequestIdRef.current = aiRequestIdRef.current + 1
    // Chat (coluna direita)
    setAiMessages([])
    setAiInput('')
    setAiLoading(false)
    setStreamingThinking('')
    setInjectedContext({ vaultTables: [], learnedSlugs: [] })
    setAiStage(null)
    setLastAiZeroRowAttempts(0)
    setLastAiZeroRowFinal(false)
    // Coluna central (SQL atual, tabela, relatórios, histórico)
    setCurrentSQL('')
    setQueryResult(null)
    setQueryError(null)
    setSortCol(null)
    setIsExecuting(false)
    setReportData(null)
    setReportView(false)
    setActiveHistoryId(null)
    setActiveMeta(null)
    // A folha do resultado não pode sobreviver ao que ela mostrava.
    setFolhaAberta(false)
  }, [])

  // ── Acompanha o corte de 1280px. Vale para giro de tablet (retrato↔paisagem
  // cruzam 820↔1180, os dois abaixo do corte) e para janela redimensionada no
  // desktop. Ao voltar para tela larga, fecha gaveta e folha: se ficassem
  // abertas, virariam um overlay preso por cima do layout de três colunas.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(max-width: 1279px)')
    const aplica = () => {
      setEstreito(mq.matches)
      estreitoRef.current = mq.matches
      if (!mq.matches) { setHistDrawer(false); setFolhaAberta(false); setMenuMobile(false) }
    }
    aplica()
    mq.addEventListener('change', aplica)
    return () => mq.removeEventListener('change', aplica)
  }, [])

  // ── O teclado do tablet cobrindo a caixa de digitação.
  // No iOS o teclado encolhe o viewport VISUAL e não o de layout: um rodapé em
  // `h-full` continua desenhado onde estava e some atrás do teclado. `100dvh`
  // não resolve — o `dvh` acompanha a barra do navegador, não o teclado. A
  // única fonte de verdade é o visualViewport, e é dele que sai a altura aqui.
  // Só abaixo de 1280px: no desktop não há teclado virtual e mexer na altura
  // por JS só criaria oportunidade de regressão.
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null
    const el = painelRef.current
    if (!el) return
    if (!estreito || !vv) { el.style.removeProperty('height'); return }
    const aplica = () => {
      // offsetTop entra porque o iOS ROLA o viewport visual para revelar o campo
      // focado; sem ele a altura fica certa e a posição errada.
      const alturaUtil = vv.height - Math.max(0, el.getBoundingClientRect().top - vv.offsetTop)
      el.style.height = `${Math.max(240, Math.round(alturaUtil))}px`
    }
    aplica()
    vv.addEventListener('resize', aplica)
    vv.addEventListener('scroll', aplica)
    return () => {
      vv.removeEventListener('resize', aplica)
      vv.removeEventListener('scroll', aplica)
      el.style.removeProperty('height')
    }
  }, [estreito])

  // Camada 7: voto na query (+1 / -1) ou promoção forçada (admin).
  // Atualiza a mensagem localmente pra trocar UI sem precisar refetch.
  const voteOnQuery = useCallback(async (messageId: string, queryId: number, value: 1 | -1) => {
    try {
      const r = await authenticatedFetch(`/api/oracle/queries/${queryId}/vote`, {
        method: 'POST',
        body: JSON.stringify({ value })
      } as RequestInit)
      const d = await r.json() as {
        promoted?: boolean; just_promoted?: boolean; vault_slug?: string | null;
        votes_up?: number; votes_down?: number
      }
      setAiMessages(p => p.map(m => m.id === messageId
        ? {
            ...m,
            userVote: value,
            promoted: d.promoted || m.promoted,
            justPromoted: !!d.just_promoted,
            votesUp: typeof d.votes_up === 'number' ? d.votes_up : m.votesUp,
            votesDown: typeof d.votes_down === 'number' ? d.votes_down : m.votesDown,
            vaultSlug: d.vault_slug ?? m.vaultSlug ?? null
          }
        : m))
      // justPromoted é animação one-shot — limpa após 6s pra não ficar piscando
      if (d.just_promoted) {
        window.setTimeout(() => {
          setAiMessages(p => p.map(m => m.id === messageId ? { ...m, justPromoted: false } : m))
        }, 6000)
      }
    } catch {
      // silencioso — voto é ação não-crítica
    }
  }, [authenticatedFetch])

  const promoteQuery = useCallback(async (messageId: string, queryId: number) => {
    try {
      const r = await authenticatedFetch(`/api/oracle/queries/${queryId}/promote`, {
        method: 'POST', body: JSON.stringify({})
      } as RequestInit)
      const d = await r.json() as {
        promoted?: boolean; just_promoted?: boolean; vault_slug?: string | null;
        votes_up?: number; votes_down?: number
      }
      setAiMessages(p => p.map(m => m.id === messageId
        ? {
            ...m,
            userVote: 1,
            promoted: !!d.promoted,
            justPromoted: !!d.just_promoted,
            votesUp: typeof d.votes_up === 'number' ? d.votes_up : m.votesUp,
            votesDown: typeof d.votes_down === 'number' ? d.votes_down : m.votesDown,
            vaultSlug: d.vault_slug ?? m.vaultSlug ?? null
          }
        : m))
      if (d.just_promoted) {
        window.setTimeout(() => {
          setAiMessages(p => p.map(m => m.id === messageId ? { ...m, justPromoted: false } : m))
        }, 6000)
      }
    } catch {
      // silencioso
    }
  }, [authenticatedFetch])

  // Quick command state is now in the dynamic section above (userQuickCmds, expandedQC, qcParams)

  const toLocalTs = (s: string) => { const iso = s.includes('T') ? s : s.replace(' ', 'T') + 'Z'; return new Date(iso) }
  const formatTime = (d: Date) => d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

  // ─── Humanize cell values ───
  const CURRENCY_HINTS = new Set(['VALOR', 'SALDO', 'FATURADO', 'CREDITO', 'DEBITO', 'PAGO', 'LIQUIDO', 'RECEBER', 'VENCER', 'ATRASADO', 'VENDA', 'PRECO', 'LIMITE', 'LANCAMENTO', 'NORMAL', 'ESPECIAL', 'FRETE', 'BRINDE', 'TOTAL', 'RECORD', 'FAT', 'ABERTO'])
  const isCurrencyCol = useCallback((colName: string) => {
    const upper = colName.toUpperCase()
    for (const hint of CURRENCY_HINTS) { if (upper.includes(hint)) return true }
    return false
  }, [])
  const isDateCol = useCallback((colType: string) => colType?.toUpperCase().includes('DATE'), [])
  const isSemanaCol = useCallback((colName: string) => colName.toUpperCase().includes('SEMANA'), [])
  // Códigos/IDs ficam em mono na tabela (CCODIGO, CPF…); o resto é sans.
  const isCodeCol = useCallback((colName: string, hint?: FormatHint) => {
    if (hint === 'id' || hint === 'cpf' || hint === 'cnpj' || hint === 'cep' || hint === 'phone') return true
    return /CODIGO|^COD_|_COD$|^ID$|_ID$|CPF|CNPJ|CEP|FONE|CELULAR/.test(colName.toUpperCase())
  }, [])
  // humanizeCell agora aceita formatHint vindo do backend (source of truth).
  // Quando hint não vem (queries muito antigas ou tabela manual), cai nas
  // heurísticas antigas via colName/colType.
  const humanizeCell = useCallback((value: unknown, colName: string, colType: string, hint?: FormatHint): string => {
    if (value === null || value === undefined) return ''
    const str = String(value)
    // Date (hint OU heurística antiga)
    if (hint === 'date' || (!hint && isDateCol(colType))) {
      try { const d = new Date(str); if (!isNaN(d.getTime())) return d.toLocaleDateString('pt-BR') } catch { /* */ }
      return str
    }
    const num = Number(value)
    // Strings formatáveis
    if (hint === 'cep' && /^\d+$/.test(str)) {
      const s = str.padStart(8, '0'); return `${s.slice(0,5)}-${s.slice(5,8)}`
    }
    if (hint === 'cpf' && /^\d+$/.test(str)) {
      const s = str.padStart(11, '0'); return `${s.slice(0,3)}.${s.slice(3,6)}.${s.slice(6,9)}-${s.slice(9,11)}`
    }
    if (hint === 'cnpj' && /^\d+$/.test(str)) {
      const s = str.padStart(14, '0'); return `${s.slice(0,2)}.${s.slice(2,5)}.${s.slice(5,8)}/${s.slice(8,12)}-${s.slice(12,14)}`
    }
    if (hint === 'phone' && /^\d+$/.test(str)) {
      if (str.length === 11) return `(${str.slice(0,2)}) ${str.slice(2,7)}-${str.slice(7,11)}`
      if (str.length === 10) return `(${str.slice(0,2)}) ${str.slice(2,6)}-${str.slice(6,10)}`
      if (str.length === 9) return `${str.slice(0,5)}-${str.slice(5,9)}`
      if (str.length === 8) return `${str.slice(0,4)}-${str.slice(4,8)}`
    }
    if (isNaN(num)) return str.trim()
    // Semana AAAASS → SS/AAAA
    if ((hint === 'semana' || (!hint && isSemanaCol(colName))) && num >= 200000 && num < 209999) {
      const year = Math.floor(num / 100)
      const week = num % 100
      return `${String(week).padStart(2, '0')}/${year}`
    }
    if (hint === 'percent') {
      return num.toLocaleString('pt-BR', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 2 })
    }
    if (hint === 'currency' || (!hint && isCurrencyCol(colName))) {
      return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    }
    // IDs ficam mono sem agrupamento (mais legível para códigos longos)
    if (hint === 'id') return String(num)
    // Plain number with thousands separator
    if (num % 1 === 0 && Math.abs(num) >= 1000) return num.toLocaleString('pt-BR')
    if (num % 1 !== 0) return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    return str
  }, [isCurrencyCol, isDateCol, isSemanaCol])

  const handleSort = useCallback((colName: string) => {
    if (sortCol === colName) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(colName); setSortDir('asc') }
  }, [sortCol])

  const sortedRows = useMemo(() => {
    if (!queryResult?.rows || !sortCol) return queryResult?.rows || []
    return [...queryResult.rows].sort((a, b) => {
      const va = a[sortCol], vb = b[sortCol]
      if (va == null && vb == null) return 0
      if (va == null) return 1
      if (vb == null) return -1
      const na = Number(va), nb = Number(vb)
      if (!isNaN(na) && !isNaN(nb)) return sortDir === 'asc' ? na - nb : nb - na
      const sa = String(va).trim(), sb = String(vb).trim()
      return sortDir === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa)
    })
  }, [queryResult?.rows, sortCol, sortDir])

  // ─── Filtro + paginação client-side ───
  // Diretoria raramente precisa ver as 50k linhas de uma vez. Paginação por
  // 100 evita travada do navegador e permite scroll fluido. Filtro de texto
  // é case-insensitive em todas as colunas — diretor digita "ricardo" e vê
  // só linhas com Ricardo, indep. de coluna.
  const [tableFilter, setTableFilter] = useState('')
  const [tablePage, setTablePage] = useState(0)
  const TABLE_PAGE_SIZE = 100
  // Resetar página/filtro quando o resultado muda (nova query)
  useEffect(() => {
    setTablePage(0); setTableFilter('')
  }, [queryResult?.historyId])
  const filteredRows = useMemo(() => {
    if (!tableFilter.trim()) return sortedRows
    const needle = tableFilter.trim().toLowerCase()
    return sortedRows.filter(row => {
      for (const k in row) {
        const v = row[k]
        if (v == null) continue
        if (String(v).toLowerCase().includes(needle)) return true
      }
      return false
    })
  }, [sortedRows, tableFilter])
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / TABLE_PAGE_SIZE))
  const safePage = Math.min(tablePage, totalPages - 1)
  const pagedRows = useMemo(
    () => filteredRows.slice(safePage * TABLE_PAGE_SIZE, (safePage + 1) * TABLE_PAGE_SIZE),
    [filteredRows, safePage]
  )

  // Tabela responsiva por FONTE: encolhe 0.5px por passada até todas as
  // colunas caberem na largura (sem corte no fim); piso 7px — abaixo disso
  // o overflow-auto segue como último recurso. Reseta a cada resultado.
  const tableScrollRef = useRef<HTMLDivElement>(null)
  const [tableFs, setTableFs] = useState(11)
  // Também rearma ao cruzar os 1280px: vindo do desktop a fonte pode estar em
  // 7px, e sem rearmar o tablet herdaria um tamanho que o piso dele proíbe.
  useEffect(() => { setTableFs(11) }, [queryResult, estreito])
  useLayoutEffect(() => {
    const el = tableScrollRef.current
    if (!el) return
    // O piso muda com a tela. No desktop encolher até 7px evita a rolagem
    // horizontal e ainda se lê de perto; num tablet na mão, 7px não se lê de
    // jeito nenhum — abaixo de 1280px o piso é 10px e quem resolve o excesso de
    // coluna é a rolagem lateral, que é o comportamento honesto ali.
    const piso = estreito ? 10 : 7
    if (el.scrollWidth > el.clientWidth + 2 && tableFs > piso) {
      setTableFs(f => Math.max(piso, +(f - 0.5).toFixed(1)))
    }
  }, [queryResult, tableFs, humanize, safePage, tableFilter, estreito])

  return (
    /* flex-col abaixo de 1280px, flex-row a partir dali. Em coluna, a barra de
       topo é o 1º filho e o chat come o resto; o histórico e o resultado saem
       do fluxo (fixed) e viram gaveta e folha. Em linha, a barra some e as três
       colunas voltam a ser exatamente o que sempre foram. */
    <div ref={painelRef} className="relative flex flex-col xl:flex-row h-full min-h-0 text-foreground neu-forms">

      {/* ═══ BARRA DE TOPO — só abaixo de 1280px ═══ */}
      {/* Ela existe porque, sem as colunas, três coisas ficariam sem casa: o
          caminho para o histórico, o LED do Oracle (que morava no header da
          sidebar) e as ações do chat (cérebro, comandos rápidos, vassoura), que
          em 390px não cabem soltas — viram um ⋯. Altura 44px: é o alvo de toque
          mínimo, e o mesmo que o mini-header do App usa logo acima. */}
      <div className="xl:hidden flex items-center gap-1 h-11 px-2 border-b border-border glass-bar neu-bar shrink-0 relative z-30">
        <button onClick={() => { setSidebarOpen(true); setHistDrawer(true) }}
          title="Histórico de consultas" aria-label="Abrir histórico de consultas"
          className="w-11 h-11 -ml-1 flex items-center justify-center rounded-lg text-muted-foreground active:bg-secondary transition-colors">
          <FiClock className="w-5 h-5" />
        </button>
        <span className="text-[13px] font-semibold text-foreground">Dados</span>
        <span
          title={`Oracle ${connectionStatus === 'connected' ? 'online' : connectionStatus === 'error' ? 'offline' : 'verificando…'}${connectionInfo ? ' — ' + connectionInfo : ''}`}
          aria-label="Status da conexão Oracle"
          className={`w-2 h-2 rounded-full shrink-0 ${connectionStatus === 'connected' ? 'bg-emerald-500' : connectionStatus === 'error' ? 'bg-red-500' : 'bg-yellow-500 animate-pulse'}`} />
        <div className="flex-1" />
        {/* Atalho para a folha do resultado corrente — sem ele, quem fechasse a
            folha só voltaria à tabela rolando o fio atrás do cartão. */}
        {(currentSQL || queryResult) && (
          <button onClick={() => setFolhaAberta(true)}
            title="Ver a consulta e o resultado" aria-label="Ver a consulta e o resultado"
            className="h-11 px-2.5 flex items-center gap-1.5 rounded-lg text-[12px] text-muted-foreground active:bg-secondary transition-colors">
            <FiDatabase className="w-4 h-4" />
            {queryResult ? queryResult.rowCount : 'SQL'}
          </button>
        )}
        <div className="relative">
          <button onClick={() => setMenuMobile(m => !m)}
            title="Mais ações" aria-label="Mais ações"
            className="w-11 h-11 flex items-center justify-center rounded-lg text-muted-foreground active:bg-secondary transition-colors">
            <FiMoreVertical className="w-5 h-5" />
          </button>
          {menuMobile && (<>
            <div className="absolute inset-0 z-40" onClick={() => setMenuMobile(false)} />
            <div className="absolute right-0 top-full mt-1 z-50 w-56 rounded-xl neu-pop bg-card border border-border py-1 shadow-lg">
              <button onClick={() => { setMenuMobile(false); setQcDropdownFilter(''); setShowQCDropdown(true) }}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[13px] text-foreground active:bg-secondary transition-colors">
                <FiCommand className="w-4 h-4 text-primary" /> Meus comandos rápidos
              </button>
              <button onClick={() => { setMenuMobile(false); handleDeepLearn() }}
                disabled={deepLearning || connectionStatus !== 'connected'}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[13px] text-foreground active:bg-secondary transition-colors disabled:opacity-50">
                <GiBrain className="w-4 h-4 text-violet-500" /> {deepLearning ? 'Aprendendo…' : 'Aprendizado profundo'}
              </button>
              {user?.isAdmin && (
                <button onClick={() => { setMenuMobile(false); setRevisaoAberta(true) }}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[13px] text-foreground active:bg-secondary transition-colors">
                  <FiCheck className="w-4 h-4 text-violet-500" /> Revisar aprendizado{revisaoN > 0 ? ` (${revisaoN})` : ''}
                </button>
              )}
              <button onClick={() => { setMenuMobile(false); limpaTudo() }}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[13px] text-foreground active:bg-secondary transition-colors">
                <GiBroom className="w-4 h-4 text-amber-500" /> Limpar tudo
              </button>
            </div>
          </>)}
        </div>
      </div>

      {/* Fundo escuro da gaveta do histórico. Fecha no toque fora — é o gesto
          que todo mundo tenta primeiro. */}
      {histDrawer && (
        <div className="xl:hidden absolute inset-0 z-40 bg-black/40" onClick={() => setHistDrawer(false)} />
      )}

      {/* ═══ LEFT SIDEBAR — Árvore de histórico de geração (autor → período) ═══ */}
      {/* Container único animado (27/07): a largura transiciona 288↔40px em vez
          do swap seco de dois divs; a filipeta gira o chevron acompanhando. */}
      <div className={`${histDrawer ? 'flex' : 'hidden'} xl:flex absolute inset-y-0 left-0 z-50 w-[86vw] max-w-xs xl:relative xl:inset-auto xl:left-auto xl:z-30 xl:max-w-none ${sidebarOpen ? 'xl:w-72' : 'xl:w-10 xl:items-center xl:py-3 xl:gap-2'} transition-[width] duration-300 ease-in-out border-r border-border flex-col glass-bar neu-bar neu-ledge-r shrink-0`}>
          {/* Fechar a gaveta. Só existe abaixo de 1280px — no desktop quem
              recolhe a coluna é a filipeta logo abaixo. */}
          <button onClick={() => setHistDrawer(false)}
            className="xl:hidden absolute right-1 top-1 z-50 w-11 h-11 flex items-center justify-center rounded-lg text-muted-foreground active:bg-secondary transition-colors"
            title="Fechar histórico" aria-label="Fechar histórico">
            <FiX className="w-5 h-5" />
          </button>
          <button onClick={() => setSidebarOpen(o => !o)}
            className="hidden xl:flex absolute right-0 translate-x-1/2 top-1/2 -translate-y-1/2 z-50 items-center justify-center w-7 h-7 rounded-full neu-tab-out-r text-muted-foreground hover:text-foreground transition-colors"
            title={sidebarOpen ? 'Recolher painel' : 'Expandir histórico'}>
            <FiChevronLeft className={`w-4 h-4 transition-transform duration-300 ease-in-out ${sidebarOpen ? '' : 'rotate-180'}`} />
          </button>
          {sidebarOpen ? (<>

          {/* Header: título da árvore + status Oracle (só um ícone on/off) */}
          <div className="px-3 py-2.5 border-b border-border flex items-center gap-2">
            <FiClock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Histórico</span>
            <span
              title={`Oracle ${connectionStatus === 'connected' ? 'online' : connectionStatus === 'error' ? 'offline' : 'verificando…'}${connectionInfo ? ' — ' + connectionInfo : ''}`}
              aria-label="Status da conexão Oracle"
              className="ml-auto flex items-center shrink-0">
              <span className={`w-2 h-2 rounded-full ${connectionStatus === 'connected' ? 'bg-emerald-500' : connectionStatus === 'error' ? 'bg-red-500' : 'bg-yellow-500 animate-pulse'} ${isTestingConnection ? 'animate-ping' : ''}`} />
            </span>
          </div>

          {/* Filtro */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
              <input type="text" placeholder="Buscar no histórico…" value={histFilter} onChange={e => setHistFilter(e.target.value)}
                className="w-full pl-6 pr-2 py-1.5 text-[11px] bg-secondary/40 border border-border rounded-full focus:outline-none focus:bg-background focus:ring-2 focus:ring-ring/30 transition-colors" />
            </div>
          </div>

          {/* Árvore autor → período → query */}
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
            {historyLoading && queryHistory.length === 0 ? (
              <div className="flex items-center justify-center py-8"><FiLoader className="w-4 h-4 animate-spin text-muted-foreground" /></div>
            ) : historyTree.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center px-3">
                <FiClock className="w-7 h-7 text-muted-foreground/40 mb-2" />
                <span className="text-[11px] text-muted-foreground">{histFilter ? 'Nada bate com a busca' : 'Nenhuma geração ainda'}</span>
              </div>
            ) : historyTree.map(author => {
              const pal = author.isClara
                ? { bg: 'bg-violet-600', name: 'text-violet-700 dark:text-violet-300' }
                : pickAuthorPalette(author.authorKey, author.isOwn)
              const aOpen = histExpandedAuthors[author.authorKey] ?? false
              return (
                /* Autor expandido vira um poço neu (inset raso): o histórico do
                   usuário fica CONTIDO no rebaixo, dividindo visualmente cada
                   pessoa. Fechado volta a ser linha chapada. No tema normal o
                   rebaixo vira só um tint de fundo (neu-inset é inerte lá). */
                <div key={author.authorKey} className={`rounded-xl overflow-hidden transition-[box-shadow,background-color] duration-200 ${aOpen ? 'neu-inset bg-secondary/25 p-1 pb-1.5' : ''}`}>
                  {/* Nó do autor */}
                  <button
                    onClick={() => setHistExpandedAuthors(p => ({ ...p, [author.authorKey]: !aOpen }))}
                    className="w-full flex items-center gap-2 px-1.5 py-1.5 rounded-lg neu-item hover:bg-secondary/60 transition-colors">
                    {aOpen ? <FiChevronDown className="w-3 h-3 text-muted-foreground shrink-0" /> : <FiChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />}
                    {/* Avatar */}
                    {author.isClara ? (
                      <span className="w-5 h-5 rounded-full bg-violet-600 flex items-center justify-center shrink-0 shadow-sm"><FiZap className="w-2.5 h-2.5 text-white" /></span>
                    ) : author.avatar ? (
                      <img src={author.avatar} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" />
                    ) : (
                      <span className={`w-5 h-5 rounded-full ${pal.bg} text-white flex items-center justify-center text-[8px] font-bold shrink-0 shadow-sm`}>{authorInitials(author.name)}</span>
                    )}
                    <span className={`text-[11px] font-semibold truncate ${pal.name}`}>{author.name}{author.isOwn && ' (você)'}</span>
                    <span className="ml-auto text-[9px] text-muted-foreground tabular-nums shrink-0">{author.count}</span>
                  </button>

                  {/* Períodos */}
                  {aOpen && author.periods.map((period, pi) => {
                    const pKey = `${author.authorKey}|${period.key}`
                    const pOpen = histExpandedPeriods[pKey] ?? (pi === 0)
                    return (
                      <div key={period.key} className="ml-2.5">
                        <button
                          onClick={() => setHistExpandedPeriods(p => ({ ...p, [pKey]: !pOpen }))}
                          className="w-full flex items-center gap-1.5 px-1.5 py-1 rounded-md hover:bg-secondary/40 transition-colors">
                          {pOpen ? <FiChevronDown className="w-2.5 h-2.5 text-muted-foreground/70 shrink-0" /> : <FiChevronRight className="w-2.5 h-2.5 text-muted-foreground/70 shrink-0" />}
                          <span className="text-[9px] uppercase tracking-wider font-medium text-muted-foreground/70">{period.label}</span>
                          <span className="ml-auto text-[9px] text-muted-foreground/40 tabular-nums">{period.items.length}</span>
                        </button>

                        {/* Itens (queries) */}
                        {pOpen && (
                          <div className="ml-3 border-l border-border/50 pl-1.5 space-y-0.5 pb-1">
                            {period.items.map(item => {
                              const isActive = activeHistoryId === item.id
                              const isOwn = !!user?.id && item.user_id === user.id
                              const title = item.label || item.user_prompt || item.query_text
                              return (
                                <div key={item.id} className={`group/h rounded-lg transition-colors ${isActive ? 'bg-primary/10 shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.3)]' : 'hover:bg-secondary/50'}`}>
                                  <button onClick={() => openHistoryItem(item, true)} className="w-full text-left px-2 py-1.5">
                                    <div className="flex items-center gap-1.5">
                                      {item.status === 'success'
                                        ? <FiCheck className="w-2.5 h-2.5 text-emerald-500 shrink-0" />
                                        : <FiX className="w-2.5 h-2.5 text-red-500 shrink-0" />}
                                      <span className={`text-[11px] truncate flex-1 ${isActive ? 'text-foreground font-medium' : 'text-foreground/80'}`} title={title}>{title}</span>
                                      <div className="flex items-center gap-1 shrink-0">
                                        {item.has_conversation === 1 && <FiMessageSquare className="w-2.5 h-2.5 text-muted-foreground/40" />}
                                        {item.has_report === 1 && <FiBarChart2 className="w-2.5 h-2.5 text-purple-500/70" />}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1.5 mt-0.5 pl-4">
                                      <span className="text-[9px] text-muted-foreground/60 tabular-nums">{item.created_at ? new Date(item.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
                                      {item.row_count !== null && item.status === 'success' && <span className="text-[9px] text-muted-foreground/50">· {item.row_count} lin</span>}
                                    </div>
                                  </button>
                                  {/* Ações no hover: relatório (qualquer item com report) + renomear/excluir (só do dono) */}
                                  {(item.has_report === 1 || isOwn) && (
                                    <div className="flex items-center gap-0.5 px-2 pb-1 opacity-0 group-hover/h:opacity-100 transition-opacity">
                                      {item.has_report === 1 && (
                                        <button onClick={(e) => { e.stopPropagation(); setActiveHistoryId(item.id); loadCachedReport(item.id) }}
                                          className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] rounded bg-purple-500/10 text-purple-500 hover:bg-purple-500/20 transition-colors"><FiBarChart2 className="w-2.5 h-2.5" /> Relatório</button>
                                      )}
                                      {isOwn && (<>
                                        <button onClick={(e) => { e.stopPropagation(); const name = prompt('Nome para esta query:', item.label || ''); if (name !== null) renameHistoryItem(item.id, name) }}
                                          className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] rounded hover:bg-secondary text-muted-foreground transition-colors"><FiCode className="w-2.5 h-2.5" /> Renomear</button>
                                        <button onClick={(e) => { e.stopPropagation(); if (confirm('Excluir esta query?')) deleteHistoryItem(item.id) }}
                                          className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] rounded hover:bg-red-500/10 text-red-400 transition-colors ml-auto"><FiTrash2 className="w-2.5 h-2.5" /> Excluir</button>
                                      </>)}
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
          </>) : (<>
          <button onClick={() => setSidebarOpen(true)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors" title="Histórico de gerações">
            <FiClock className="w-4 h-4" />
          </button>
          {/* Trilho de autores (avatar/sigla) — mesmo padrão do rail recolhido do
              WhatsApp: preserva quem tem histórico em vez de deixar a coluna vazia.
              Clique abre a sidebar já expandida naquela pessoa. */}
          <div className="flex-1 min-h-0 overflow-y-auto flex flex-col items-center gap-1.5 scrollbar-thin">
            {historyTree.map(author => {
              const pal = author.isClara
                ? { bg: 'bg-violet-600' }
                : pickAuthorPalette(author.authorKey, author.isOwn)
              return (
                <button key={author.authorKey}
                  onClick={() => { setSidebarOpen(true); setHistExpandedAuthors(p => ({ ...p, [author.authorKey]: true })) }}
                  title={`${author.name} · ${author.count} ${author.count === 1 ? 'consulta' : 'consultas'}`}
                  className="shrink-0 rounded-full hover:ring-2 hover:ring-ring/40 transition-shadow">
                  {author.isClara ? (
                    <span className="w-6 h-6 rounded-full bg-violet-600 flex items-center justify-center shadow-sm"><FiZap className="w-3 h-3 text-white" /></span>
                  ) : author.avatar ? (
                    <img src={author.avatar} alt="" className="w-6 h-6 rounded-full object-cover" />
                  ) : (
                    <span className={`w-6 h-6 rounded-full ${pal.bg} text-white flex items-center justify-center text-[9px] font-bold shadow-sm`}>{authorInitials(author.name)}</span>
                  )}
                </button>
              )
            })}
          </div>
          <div className={`w-2 h-2 rounded-full shrink-0 ${connectionStatus === 'connected' ? 'bg-emerald-500' : connectionStatus === 'error' ? 'bg-red-500' : 'bg-yellow-500'}`} title={`Oracle ${connectionStatus === 'connected' ? 'online' : connectionStatus === 'error' ? 'offline' : 'verificando'}`} />
          </>)}
      </div>

      {/* ═══ CENTER: Results (70%) ═══ */}
      {/* neu-field-in: campo de trabalho pontilhado e levemente afundado —
          separa o centro dos dois trilhos laterais sem mais um nivel de relevo. */}
      <div className={`${folhaAberta ? 'flex' : 'hidden'} xl:flex absolute inset-0 z-50 bg-card xl:relative xl:inset-auto xl:z-auto xl:bg-transparent flex-[7] flex-col min-w-0 neu-field-in`}>
        {/* Cabeçalho da folha: só abaixo de 1280px. No desktop esta coluna está
            sempre à vista e não precisa de um jeito de sair dela. */}
        <div className="xl:hidden flex items-center gap-2 h-11 px-2 border-b border-border glass-bar shrink-0">
          <button onClick={() => setFolhaAberta(false)}
            className="w-11 h-11 -ml-1 flex items-center justify-center rounded-lg text-muted-foreground active:bg-secondary transition-colors"
            title="Voltar para a conversa" aria-label="Voltar para a conversa">
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <span className="text-[13px] font-semibold text-foreground truncate">
            {reportView ? 'Relatório' : 'Resultado'}
          </span>
          {queryResult && (
            <span className="text-[12px] text-muted-foreground shrink-0">
              {queryResult.rowCount} {queryResult.rowCount === 1 ? 'linha' : 'linhas'} · {queryResult.columns.length} col.
            </span>
          )}
        </div>
        {/* Header do centro só existe no modo Relatório (27/07): fora dele o
            campo pontilhado toma esse espaço e o card respira igual em cima e
            embaixo. É aqui que vivem o voltar-pra-tabela + exports do relatório. */}
        {reportView && (
        <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border glass-bar shrink-0">
          <span className="text-xs text-muted-foreground flex items-center gap-1.5"><FiDatabase className="w-3.5 h-3.5" /> Dados</span>
          <div className="flex-1" />
          {/* Ações migraram pra DENTRO do bloco central (27/07) — aqui só ficam
              no modo Relatório, que é full-bleed e precisa do botão de voltar. */}
          {reportView && queryResult && queryResult.rows.length > 0 && (<>
            <button onClick={copyResults} className="flex items-center gap-1 px-2 py-1 text-[11px] rounded neu-convex hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"><FiCopy className="w-3 h-3" /> Copiar</button>
            <button onClick={exportCSV} className="flex items-center gap-1 px-2 py-1 text-[11px] rounded neu-convex hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"><FiDownload className="w-3 h-3" /> CSV</button>
            <button onClick={exportTXT} className="flex items-center gap-1 px-2 py-1 text-[11px] rounded neu-convex hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"><FiFileText className="w-3 h-3" /> TXT</button>
            <button onClick={exportXLSX} className="flex items-center gap-1 px-2 py-1 text-[11px] rounded neu-convex hover:bg-secondary text-muted-foreground hover:text-foreground font-medium transition-colors"><FiDownload className="w-3 h-3 text-emerald-500" /> Excel</button>
            <button onClick={() => exportReportPDF()} disabled={pdfLoading}
              title="Transformar em relatório PDF — página de celular, com leitura analítica"
              className="flex items-center gap-1 px-2 py-1 text-[11px] rounded neu-convex hover:bg-secondary text-muted-foreground hover:text-foreground font-medium transition-colors disabled:opacity-60">
              {pdfLoading ? <FiLoader className="w-3 h-3 animate-spin text-amber-500" /> : <FiFileText className="w-3 h-3 text-amber-500" />}
              {pdfLoading ? 'Gerando…' : 'Relatório PDF'}
            </button>
            {exportXlsxError && (
              <span className="text-[10px] text-red-500 ml-1" role="alert">⚠ {exportXlsxError}</span>
            )}
            <div className="w-px h-4 bg-border mx-1" />
            <button onClick={() => setHumanize(h => !h)}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-[11px] rounded font-medium transition-all ${
                humanize ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-secondary/60 text-muted-foreground hover:bg-secondary hover:text-foreground'
              }`}
              aria-pressed={humanize}
              title={humanize ? 'Mostrando valores formatados (R$, datas, %). Clique para ver dados brutos do Oracle.' : 'Mostrando dados brutos do Oracle. Clique para formatar (R$, datas, %).'}>
              <div className={`w-6 h-3.5 rounded-full relative transition-colors ${humanize ? 'bg-emerald-400' : 'bg-muted-foreground/30'}`}>
                <div className={`absolute top-0.5 w-2.5 h-2.5 rounded-full bg-white shadow-sm transition-all ${humanize ? 'left-3' : 'left-0.5'}`} />
              </div>
              {humanize ? 'Formatado' : 'Bruto'}
            </button>
            <div className="w-px h-4 bg-border mx-1" />
            <button onClick={reportView ? () => setReportView(false) : generateReport}
              disabled={reportLoading}
              className={`flex items-center gap-1 px-2.5 py-1 text-[11px] rounded font-medium transition-colors ${
                reportView ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-purple-600/10 text-purple-500 hover:bg-purple-600/20 border border-purple-500/30'
              }`}>
              {reportLoading ? <FiLoader className="w-3 h-3 animate-spin" /> : reportView ? <FiArrowLeft className="w-3 h-3" /> : <FiBarChart2 className="w-3 h-3" />}
              {reportLoading ? 'Gerando...' : reportView ? 'Tabela' : 'Relatório'}
            </button>
            <div className="relative group/star">
              <button onClick={handleSaveAsQC} disabled={savingQC}
                className="flex items-center gap-1 px-2 py-1 text-[11px] rounded neu-convex hover:bg-secondary text-primary transition-colors">
                {savingQC ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiPlusCircle className="w-3.5 h-3.5 text-primary" />}
              </button>
              <div className="absolute right-0 top-full mt-1.5 w-48 px-2.5 py-1.5 rounded-lg bg-foreground text-background text-[10px] leading-relaxed opacity-0 group-hover/star:opacity-100 pointer-events-none transition-opacity z-30 shadow-lg">
                Salvar como comando rápido para reutilizar sempre que quiser
                <div className="absolute -top-1 right-3 w-2 h-2 bg-foreground rotate-45" />
              </div>
            </div>
          </>)}
        </div>
        )}

        {/* Status bar removida (27/07): linhas/tempo, erro e "executando" agora
            vivem dentro do bloco central — evita informação duplicada. */}

        {/* Results / Report toggle */}
        <div className="flex-1 overflow-auto">
          {reportView ? (
            /* ═══ SYNTHETIC REPORT ═══ */
            reportLoading ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <FiLoader className="w-8 h-8 animate-spin mb-3 text-purple-500" />
                <span className="text-sm font-medium">Gerando relatório sintético...</span>
                <span className="text-xs text-muted-foreground mt-1">A IA está analisando {queryResult?.rowCount} linhas de dados</span>
              </div>
            ) : reportData ? (
              <div className="p-4 space-y-4">
                {/* Report header */}
                <div className="glass-panel neu-card rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/15 flex items-center justify-center shrink-0">
                      <FiBarChart2 className="w-5 h-5 text-purple-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-base font-bold text-foreground leading-tight">{reportData.title}</h2>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{reportData.summary}</p>
                    </div>
                  </div>
                </div>

                {/* KPI Cards */}
                {reportData.kpis?.length > 0 && (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {reportData.kpis.map((kpi, i) => {
                      const Icon = KPI_ICONS[kpi.icon || 'hash'] || FiHash
                      return (
                        <div key={i} className="glass-panel neu-card rounded-xl p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: (kpi.color || CHART_COLORS[i]) + '20' }}>
                              <Icon className="w-3.5 h-3.5" style={{ color: kpi.color || CHART_COLORS[i] }} />
                            </div>
                            <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium truncate">{kpi.label}</span>
                          </div>
                          <div className="text-xl font-bold text-foreground pl-1">{kpi.value}</div>
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* Charts */}
                {reportData.charts?.length > 0 && (
                  <div className={`grid gap-4 ${reportData.charts.length === 1 ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
                    {reportData.charts.map((chart, ci) => (
                      <div key={ci} className="glass-panel neu-card rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-foreground mb-3">{chart.title}</h3>
                        <div style={{ height: chart.type === 'pie' ? 260 : 220 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            {chart.type === 'pie' ? (
                              <PieChart>
                                <Pie data={chart.data} dataKey={chart.dataKey || 'value'} nameKey={chart.categoryKey || 'name'}
                                  cx="50%" cy="50%" outerRadius={90} innerRadius={45} paddingAngle={2} label={({ name, percent }: { name?: string; percent?: number }) => `${(name || '').toString().slice(0, 12)} ${((percent || 0) * 100).toFixed(0)}%`}
                                  labelLine={false} style={{ fontSize: '10px' }}>
                                  {chart.data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                                </Pie>
                                <Tooltip />
                              </PieChart>
                            ) : chart.type === 'area' ? (
                              <AreaChart data={chart.data}>
                                <defs><linearGradient id={`grad-${ci}`} x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor={chart.color || CHART_COLORS[ci]} stopOpacity={0.3} />
                                  <stop offset="95%" stopColor={chart.color || CHART_COLORS[ci]} stopOpacity={0} />
                                </linearGradient></defs>
                                <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                                <XAxis dataKey={chart.categoryKey || 'name'} tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip />
                                <Area type="monotone" dataKey={chart.dataKey || 'value'} stroke={chart.color || CHART_COLORS[ci]} fill={`url(#grad-${ci})`} strokeWidth={2} />
                              </AreaChart>
                            ) : chart.type === 'horizontal_bar' ? (
                              <BarChart data={chart.data} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                                <XAxis type="number" tick={{ fontSize: 10 }} />
                                <YAxis type="category" dataKey={chart.categoryKey || 'name'} tick={{ fontSize: 10 }} width={100} />
                                <Tooltip />
                                <Bar dataKey={chart.dataKey || 'value'} fill={chart.color || CHART_COLORS[ci]} radius={[0, 4, 4, 0]} />
                              </BarChart>
                            ) : (
                              <BarChart data={chart.data}>
                                <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                                <XAxis dataKey={chart.categoryKey || 'name'} tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip />
                                <Bar dataKey={chart.dataKey || 'value'} radius={[4, 4, 0, 0]} animationDuration={800}>
                                  {chart.data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                                </Bar>
                              </BarChart>
                            )}
                          </ResponsiveContainer>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Footer */}
                <div className="text-center text-[10px] text-muted-foreground py-2">
                  Relatório gerado por IA a partir de {queryResult?.rowCount} registros
                </div>
              </div>
            ) : null
          ) : currentSQL ? (
            /* ═══ Bloco único da consulta (pedido 27/07): SQL + Executar + resultado
                contidos no MESMO cartão — o resultado deixa de ser full-bleed e o
                cartão alarga quando há linhas. Sem o anel de vidro (inset ring):
                só sombra de elevação, pra sentar como parte do painel. ═══ */
            <div className="h-full overflow-hidden px-4 py-5 flex flex-col items-center">
              {/* card-shadow NÃO é decorativo aqui: no fin o neu mata o box-shadow
                  de .glass-panel:not(.card-shadow) — sem essa classe o bloco fica
                  solto (divergência vista em 27/07). max-h-full + pb-5 do pai =
                  o card expande até ~20px do fundo; tabela curta encerra antes. */}
              <div className={`w-full ${queryResult && queryResult.rows.length > 0 ? 'max-w-6xl' : 'max-w-3xl'} glass-panel neu-card card-shadow rounded-2xl p-5 flex flex-col min-h-0 max-h-full !shadow-[0_1px_2px_0_rgba(2,6,23,0.15),0_4px_10px_-2px_rgba(2,6,23,0.10),0_10px_22px_-4px_rgba(2,6,23,0.07),0_18px_36px_-8px_rgba(2,6,23,0.04)] transition-all duration-300`}>
                <div className="flex items-center gap-2.5 mb-3 shrink-0">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center shrink-0">
                    <FiCode className="w-4 h-4 text-emerald-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-foreground leading-tight">Consulta carregada</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {activeMeta?.authorName ? `Gerada por ${activeMeta.authorName}` : 'Pronta para executar'}
                      {activeMeta?.createdAt ? ` · ${new Date(activeMeta.createdAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : ''}
                    </div>
                  </div>
                  <button
                    onClick={() => executeSQL(currentSQL)}
                    disabled={isExecuting || connectionStatus !== 'connected'}
                    title={connectionStatus !== 'connected' ? 'Oracle offline — não dá pra executar agora' : 'Executar esta consulta no Oracle'}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-emerald-600 neu-send hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0">
                    <FiZap className="w-3.5 h-3.5" /> Executar
                  </button>
                </div>
                {activeMeta?.prompt && (
                  <div className="mb-3 px-3 py-2 rounded-lg bg-blue-500/5 border-l-2 border-blue-500/40 text-[12px] text-blue-700 dark:text-blue-300 italic leading-relaxed shrink-0">
                    “{activeMeta.prompt}”
                  </div>
                )}
                {/* SQL: generoso quando o card é só a consulta; encolhe (scroll
                    interno) quando o resultado precisa do espaço. O wrapper
                    relative fica FORA do scroller pro botão de copiar não rolar
                    junto com o código. */}
                <div className="relative group/sqlpit shrink-0">
                  <div className={`rounded-xl neu-pit p-3 overflow-auto ${queryResult || isExecuting ? 'max-h-40' : 'max-h-[45vh]'}`}>
                    {/* `<pre>` nasce `white-space: pre`: linha longa de SQL não
                        quebra, ela sai pela direita e a cauda fica escondida
                        atrás do scroll horizontal. `pre-wrap` quebra SÓ o que
                        estoura e PRESERVA a indentação original — que é o que
                        deixa a query legível. `break-words` cobre o caso de um
                        token único gigante (lista de IDs, string longa).
                        NÃO usar `text-indent` pra pendurar a continuação: ele
                        vale pra primeira linha do bloco inteiro, não por linha
                        visual, e desloca a query toda. */}
                    <pre className="text-[11px] font-mono leading-relaxed m-0 text-zinc-200 pr-8 whitespace-pre-wrap break-words" dangerouslySetInnerHTML={{ __html: hlSQL(currentSQL) }} />
                  </div>
                  <button
                    onClick={() => { navigator.clipboard.writeText(currentSQL).then(() => { setSqlCopied(true); setTimeout(() => setSqlCopied(false), 1500) }).catch(() => {}) }}
                    title="Copiar SQL" aria-label="Copiar SQL"
                    className="absolute top-2 right-2 w-7 h-7 inline-flex items-center justify-center rounded-md bg-white/10 hover:bg-white/20 text-zinc-400 hover:text-white opacity-0 group-hover/sqlpit:opacity-100 focus-visible:opacity-100 transition-opacity">
                    {sqlCopied ? <FiCheck className="w-3.5 h-3.5 text-emerald-400" /> : <FiCopy className="w-3.5 h-3.5" />}
                  </button>
                </div>
                {isExecuting ? (
                  <div className="mt-4 py-8 flex flex-col items-center justify-center text-muted-foreground">
                    <FiLoader className="w-6 h-6 animate-spin mb-2 text-emerald-500" />
                    <span className="text-xs">Executando no Oracle…</span>
                  </div>
                ) : queryResult && queryResult.rows.length > 0 ? (<>
              <div className="mt-4 mb-2 flex flex-wrap items-center gap-2 shrink-0">
                <div className="relative flex-1 max-w-xs">
                  <FiSearch className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={tableFilter}
                    onChange={e => { setTableFilter(e.target.value); setTablePage(0) }}
                    placeholder="Filtrar resultado..."
                    className="w-full pl-7 pr-7 py-1 text-[11px] rounded-full border border-border bg-secondary/40 focus:bg-background focus:ring-2 focus:ring-ring/30 focus:border-border outline-none transition-colors"
                    aria-label="Filtrar linhas do resultado"
                  />
                  {tableFilter && (
                    <button
                      onClick={() => { setTableFilter(''); setTablePage(0) }}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-secondary text-muted-foreground"
                      title="Limpar filtro"
                      aria-label="Limpar filtro"
                    ><FiX className="w-3 h-3" /></button>
                  )}
                </div>
                <span className="text-[10px] text-muted-foreground tabular-nums whitespace-nowrap">
                  {tableFilter
                    ? `${filteredRows.length.toLocaleString('pt-BR')} de ${queryResult.rowCount.toLocaleString('pt-BR')}`
                    : `${queryResult.rowCount.toLocaleString('pt-BR')} linhas`} · {queryResult.executionTimeMs}ms
                  {queryResult.maxRowsReached && <span className="text-yellow-500" title="Limite de 50000 linhas atingido"> · limite 50k</span>}
                </span>
                {totalPages > 1 && (
                  <div className="flex items-center gap-0.5 text-[10px] text-muted-foreground tabular-nums bg-secondary/60 border border-border rounded-full px-1 py-0.5">
                    <button
                      onClick={() => setTablePage(0)}
                      disabled={safePage === 0}
                      className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      title="Primeira página"
                      aria-label="Primeira página"
                    >«</button>
                    <button
                      onClick={() => setTablePage(p => Math.max(0, p - 1))}
                      disabled={safePage === 0}
                      className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      title="Página anterior"
                      aria-label="Página anterior"
                    ><FiChevronLeft className="w-3 h-3" /></button>
                    <span className="px-1.5">Pág. {safePage + 1} / {totalPages}</span>
                    <button
                      onClick={() => setTablePage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={safePage >= totalPages - 1}
                      className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      title="Próxima página"
                      aria-label="Próxima página"
                    ><FiChevronRight className="w-3 h-3" /></button>
                    <button
                      onClick={() => setTablePage(totalPages - 1)}
                      disabled={safePage >= totalPages - 1}
                      className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      title="Última página"
                      aria-label="Última página"
                    >»</button>
                  </div>
                )}
                {/* Ações do resultado — só ícones (minimalista, 27/07): copiar/
                    CSV/TXT/Excel, chave Formatado, Relatório e salvar comando
                    rápido migraram da top bar pra dentro do bloco. */}
                <div className="ml-auto flex items-center gap-1">
                  {exportXlsxError && (
                    <span className="text-[10px] text-red-500" role="alert">⚠ {exportXlsxError}</span>
                  )}
                        <button onClick={copyResults} title="Copiar resultado" aria-label="Copiar resultado"
                    className="w-7 h-7 inline-flex items-center justify-center rounded-lg neu-convex hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"><FiCopy className="w-3.5 h-3.5" /></button>
                  <button onClick={exportCSV} title="Baixar CSV" aria-label="Baixar CSV"
                    className="w-7 h-7 inline-flex items-center justify-center rounded-lg neu-convex hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"><FiDownload className="w-3.5 h-3.5" /></button>
                  <button onClick={exportTXT} title="Baixar TXT" aria-label="Baixar TXT"
                    className="w-7 h-7 inline-flex items-center justify-center rounded-lg neu-convex hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"><FiFileText className="w-3.5 h-3.5" /></button>
                  <button onClick={exportXLSX} title="Baixar Excel" aria-label="Baixar Excel"
                    className="w-7 h-7 inline-flex items-center justify-center rounded-lg neu-convex hover:bg-secondary transition-colors"><FiDownload className="w-3.5 h-3.5 text-emerald-500" /></button>
                  <button onClick={() => exportReportPDF()} disabled={pdfLoading}
                    title="Transformar em relatório PDF — página de celular, com leitura analítica"
                    aria-label="Transformar em relatório PDF"
                    className="w-7 h-7 inline-flex items-center justify-center rounded-lg bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 border border-amber-500/30 transition-colors disabled:opacity-60">
                    {pdfLoading ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiFileText className="w-3.5 h-3.5" />}
                  </button>
                  <div className="w-px h-4 bg-border mx-0.5" />
                  <button onClick={() => setHumanize(h => !h)} aria-pressed={humanize}
                    title={humanize ? 'Mostrando valores formatados (R$, datas, %). Clique para ver dados brutos do Oracle.' : 'Mostrando dados brutos do Oracle. Clique para formatar (R$, datas, %).'}
                    className="h-7 px-0.5 inline-flex items-center justify-center">
                    <div className={`w-7 h-4 rounded-full relative transition-colors ${humanize ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`}>
                      <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow-sm transition-all ${humanize ? 'left-3.5' : 'left-0.5'}`} />
                    </div>
                  </button>
                  <div className="w-px h-4 bg-border mx-0.5" />
                  <button onClick={reportView ? () => setReportView(false) : generateReport} disabled={reportLoading}
                    title={reportView ? 'Voltar à tabela' : 'Gerar relatório sintético com IA'} aria-label="Relatório"
                    className={`w-7 h-7 inline-flex items-center justify-center rounded-lg transition-colors disabled:opacity-60 ${reportView ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-purple-600/10 text-purple-500 hover:bg-purple-600/20 border border-purple-500/30'}`}>
                    {reportLoading ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiBarChart2 className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={handleSaveAsQC} disabled={savingQC}
                    title="Salvar como comando rápido para reutilizar sempre que quiser" aria-label="Salvar como comando rápido"
                    className="w-7 h-7 inline-flex items-center justify-center rounded-lg neu-convex hover:bg-secondary text-primary transition-colors">
                    {savingQC ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiPlusCircle className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
                {/* bg sólido + translateZ de propósito: blur/máscara arredondada em
                    volta de scroller+sticky branqueia a área em GPU real (classe de
                    bug já vista no projeto). O translateZ isola o scroller em camada
                    própria — workaround canônico do blank-content no clip arredondado. */}
                <div className="bg-card neu-table rounded-2xl overflow-hidden min-h-0 flex [isolation:isolate]">
                  <div ref={tableScrollRef} className="w-full min-h-0 overflow-auto [transform:translateZ(0)]">
            <table className="w-full border-collapse text-foreground" style={{ fontSize: tableFs }}>
              <thead className="sticky top-0 z-10 neu-thead">
                <tr className="bg-card neu-thead">
                  <th className="px-2.5 py-2 text-left font-semibold text-muted-foreground border-b border-border w-10">#</th>
                  {queryResult.columns.map(col => (
                    <th key={col.name}
                      onClick={() => handleSort(col.name)}
                      className="px-2.5 py-2 text-left font-semibold border-b border-border whitespace-nowrap cursor-pointer hover:bg-secondary/50 select-none transition-colors"
                      title={`${col.type} — Clique para ordenar`}>
                      <span className="flex items-center gap-1">
                        {col.name}
                        {sortCol === col.name && (
                          <span className="text-[0.9em] text-emerald-500 ml-auto shrink-0">{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>
                        )}
                        {sortCol !== col.name && (
                          <span className="text-[0.9em] text-muted-foreground/30 ml-auto shrink-0">{'\u25B2'}</span>
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>{pagedRows.map((row, i) => {
                const absoluteIdx = safePage * TABLE_PAGE_SIZE + i
                return (
                <tr key={absoluteIdx} className={`border-b border-border/40 hover:bg-secondary/60 transition-colors ${absoluteIdx % 2 === 0 ? '' : 'bg-muted/25'}`}>
                  <td className="px-2.5 py-1 text-muted-foreground/70 font-mono text-[0.9em]">{absoluteIdx + 1}</td>
                  {queryResult.columns.map(col => {
                    const raw = row[col.name]
                    const isNum = raw !== null && raw !== undefined && !isNaN(Number(raw))
                    const display = raw === null ? null : humanize ? humanizeCell(raw, col.name, col.type, col.formatHint) : String(raw)
                    const isCurrency = col.formatHint === 'currency' || (!col.formatHint && isCurrencyCol(col.name))
                    const isCode = isCodeCol(col.name, col.formatHint)
                    return (
                      <td key={col.name} className={`px-2.5 py-1 whitespace-nowrap max-w-xs truncate ${isCode ? 'font-mono' : isNum ? 'text-right tabular-nums' : ''} ${humanize && isCurrency && isNum ? 'text-emerald-700 dark:text-emerald-400 font-semibold' : ''}`}>
                        {raw === null ? <span className="text-muted-foreground/40 italic">NULL</span> : display}
                      </td>
                    )
                  })}
                </tr>
              )})}</tbody>
            </table>
                {filteredRows.length === 0 && tableFilter && (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground text-xs">
                    <FiSearch className="w-8 h-8 mb-2 opacity-30" />
                    Nenhuma linha corresponde a "{tableFilter}".
                  </div>
                )}
                  </div>
                </div>
                </>
              ) : queryResult ? (
                <div className="mt-2 py-8 flex flex-col items-center text-muted-foreground px-6 text-center">
              {lastAiZeroRowFinal ? (
                <>
                  <FiAlertTriangle className="w-10 h-10 mb-2 text-amber-500 opacity-80" />
                  <span className="text-sm font-medium text-foreground">A IA não conseguiu trazer dados nesse recorte</span>
                  <span className="text-xs mt-1">
                    Tempo: {queryResult.executionTimeMs}ms · A IA tentou {lastAiZeroRowAttempts === 1 ? '1 variação' : `${lastAiZeroRowAttempts} variações`} sem encontrar linhas.
                  </span>
                  <span className="text-[11px] mt-3 opacity-80 max-w-md leading-relaxed">
                    O SQL tecnicamente é válido — Oracle aceitou — mas os filtros não casam com nenhum registro. Possíveis causas:
                    <br />· <strong>Filtro de período num intervalo sem dados</strong> (ex.: "semana atual" caiu numa semana ainda sem fechamento na tabela fato).
                    <br />· <strong>Literal de texto com case/acento diferente</strong> ('Aguias' no prompt vs 'AGUIAS' no banco — tente reescrever sem usar nomes específicos).
                    <br />· <strong>JOIN restritivo</strong> deixou de fora exatamente o subconjunto que você quer.
                    <br /><br />
                    <strong>Sugestão:</strong> reformule a pergunta sem fixar valores literais (ex.: peça "última semana com fechamento", não "semana 202620") ou amplie o período.
                  </span>
                </>
              ) : (
                <>
                  <FiCheck className="w-10 h-10 mb-2 text-emerald-500 opacity-70" />
                  <span className="text-sm font-medium text-foreground">Query executada — 0 linhas retornadas</span>
                  <span className="text-xs mt-1">Tempo: {queryResult.executionTimeMs}ms · {queryResult.columns.length} coluna{queryResult.columns.length !== 1 ? 's' : ''} declarada{queryResult.columns.length !== 1 ? 's' : ''}</span>
                  <span className="text-[11px] mt-3 opacity-80 max-w-md leading-relaxed">
                    A consulta rodou com sucesso mas não encontrou registros. Causas comuns no DRACO:
                    <br />· comparação <code className="px-1 py-0.5 rounded bg-secondary font-mono">=</code> com letra diferente do banco (Oracle é case-sensitive em strings — tente <code className="px-1 py-0.5 rounded bg-secondary font-mono">UPPER(...)</code>);
                    <br />· período / códigos sem dados no recorte;
                    <br />· filtro de subquery retornando NULL (ex.: distrito não encontrado).
                  </span>
                </>
              )}
            </div>
              ) : queryError ? (
                <div className="mt-4 px-3 py-2 rounded-lg bg-red-500/10 border-l-2 border-red-500/50 text-[11px] text-red-700 dark:text-red-300 font-mono leading-relaxed">
                  {queryError}
                </div>
              ) : (
                <div className="mt-2.5 flex items-center gap-1.5 text-[10px] text-muted-foreground">
                  <FiDatabase className="w-3 h-3" /> O Oracle só é consultado quando você clica em <span className="font-medium text-foreground/70">Executar</span>.
                </div>
              )}
              </div>
            </div>
          ) : !queryError && !isExecuting ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <FiClock className="w-14 h-14 mb-4 opacity-10" />
              <span className="text-base font-medium mb-1 opacity-60">Histórico de gerações</span>
              <span className="text-xs opacity-40">Escolha uma consulta na árvore ou peça uma nova ao assistente IA</span>
            </div>
          ) : null}
        </div>
      </div>

      {/* ═══ RIGHT: AI Chat (30%) ═══ */}
      {/* Container único animado (27/07), mesma receita da side esquerda:
          flex-grow transiciona 3↔0 (largura desliza), filipeta gira o chevron.
          glass-bar neu-bar neu-ledge-l = sombra suave + fio de luz na borda. */}
      {/* `neu-ledge-l` NÃO leva prefixo de breakpoint. Ela é classe do index.css,
          não utilidade do Tailwind: `xl:neu-ledge-l` não gera regra nenhuma e
          some com o relevo da divisória — foi assim que 0,53% dos pixels do
          desktop mudaram sem que nada no conteúdo mudasse. Só utilidade do
          Tailwind aceita variante responsiva. */}
      <div className={`flex-1 min-h-0 w-full ${chatOpen ? 'xl:flex-[3_1_0%]' : 'xl:flex-[0_0_2.5rem] xl:w-10 xl:items-center xl:py-3 xl:gap-2'} transition-all duration-300 ease-in-out flex flex-col xl:border-l border-border min-w-0 relative z-30 glass-bar neu-bar neu-ledge-l`}>
        <button onClick={() => setChatOpen(o => !o)}
          className="hidden xl:flex absolute left-0 -translate-x-1/2 top-1/2 -translate-y-1/2 z-50 items-center justify-center w-7 h-7 rounded-full neu-tab-out-l text-muted-foreground hover:text-foreground transition-colors"
          title={chatOpen ? 'Recolher assistente' : 'Expandir assistente'}>
          <FiChevronRight className={`w-4 h-4 transition-transform duration-300 ease-in-out ${chatOpen ? '' : 'rotate-180'}`} />
        </button>
        {(chatOpen || estreito) ? (<>
        {/* Header */}
        {/* Header minimalizado: o rotulo sai (o icone + o contexto ja dizem o que e),
            a barra encolhe e as acoes viram moedinhas so-icone — mesma receita
            do header do fin. */}
        {/* neu-ledge-b: a divisoria que faltava. Um border-b de 1px nao segurava —
            as bolhas subiam e encostavam nos botoes sem transicao. A ledge poe
            sombra projetada PARA BAIXO mais o fio de luz na aresta, a mesma
            gramatica das ledges laterais do painel. Precisa de relative z-10:
            sem isso o scroller, irmao posterior no DOM, pinta por cima da sombra. */}
        <div className={`relative z-10 ${showQCDropdown ? 'flex' : 'hidden'} xl:flex items-center gap-1.5 px-2 py-1.5 border-b border-border glass-bar neu-ledge-b-scroll shrink-0`}>
          {/* O cerebro do aprendizado profundo ocupa o lugar de destaque que era
              de um FiZap puramente decorativo. Aqui o disco nao e enfeite: a cor
              do FUNDO e o status da memoria (verde/ambar/rosa), legivel de longe —
              antes isso vivia no traco de um icone de 14px na outra ponta. */}
          <div className="relative group/brain shrink-0">
            <button onClick={handleDeepLearn} disabled={deepLearning || connectionStatus !== 'connected'}
              title="Aprendizado profundo do histórico"
              aria-label="Aprendizado profundo do histórico"
              className={`w-6 h-6 rounded-full neu-relief flex items-center justify-center shrink-0 transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
                deepLearning ? 'bg-muted text-muted-foreground' :
                deepLearnDone ? 'bg-emerald-500 text-white' :
                brainStatus === 'green' ? 'bg-emerald-500 text-white' :
                brainStatus === 'yellow' ? 'bg-amber-500 text-white' :
                brainStatus === 'red' ? 'bg-rose-500 text-white' :
                'bg-primary text-primary-foreground'
              }`}>
              {deepLearning
                ? <FiLoader className="w-3.5 h-3.5 animate-spin" />
                : deepLearnDone
                  ? <FiCheck className="w-3.5 h-3.5" />
                  : <GiBrain className="w-3.5 h-3.5" />
              }
            </button>
            <div className="absolute left-0 top-full mt-1.5 w-60 px-2.5 py-2 rounded-lg bg-foreground text-background text-[10px] leading-relaxed opacity-0 group-hover/brain:opacity-100 pointer-events-none transition-opacity z-50 shadow-lg space-y-1">
              {deepLearnDone ? (
                <p className="font-medium">Memória enriquecida! Próximas consultas serão mais precisas.</p>
              ) : (
                <>
                  <p className="font-medium">Aprendizado profundo do histórico</p>
                  {brainMeta?.neverLearned && <p className="opacity-70">Nunca executado — clique para analisar o histórico.</p>}
                  {!brainMeta?.neverLearned && brainMeta?.lastLearnedAt && (
                    <p className="opacity-70">Último: {toLocalTs(brainMeta.lastLearnedAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'America/Campo_Grande' })}</p>
                  )}
                  {!brainMeta?.neverLearned && (brainMeta?.queriesSinceLast ?? 0) > 0 && (
                    <p className="opacity-70">{brainMeta!.queriesSinceLast} consulta{brainMeta!.queriesSinceLast !== 1 ? 's' : ''} desde o último aprendizado</p>
                  )}
                  {/* O balão é `bg-foreground`, que INVERTE com o tema: quase-preto no
                      claro, quase-branco no escuro. Os tons -400 foram escolhidos pro
                      fundo quase-preto e somem quando ele vira quase-branco (medido em
                      1.84:1 no estudo do tema escuro). O `dark:` desce dois degraus e
                      devolve o contraste sem mexer no claro. */}
                  {brainStatus === 'green' && <p className="text-emerald-400 dark:text-emerald-700 font-medium">Memória atualizada</p>}
                  {brainStatus === 'yellow' && <p className="text-amber-400 dark:text-amber-700 font-medium">Atualização recomendada</p>}
                  {brainStatus === 'red' && <p className="text-rose-400 dark:text-rose-700 font-medium">Atualização necessária</p>}
                </>
              )}
              <div className="absolute -top-1 left-3 w-2 h-2 bg-foreground rotate-45" />
            </div>
          </div>
          {user?.isAdmin && (
            <button onClick={() => setRevisaoAberta(true)}
              title="Revisar o que o painel aprendeu das conversas"
              aria-label="Revisar o que o painel aprendeu das conversas"
              className="h-6 px-2 inline-flex items-center gap-1 rounded-full neu-convex text-[11px] text-muted-foreground hover:text-foreground transition-colors shrink-0">
              Revisar
              {revisaoN > 0 && (
                <span className="min-w-[16px] h-4 px-1 inline-flex items-center justify-center rounded-full bg-violet-500 text-white text-[10px]">{revisaoN}</span>
              )}
            </button>
          )}
          <div className="flex-1 min-w-0" />
          {/* Quick Commands dropdown trigger */}
          {userQuickCmds.length > 0 && (
            <div className="relative">
              <button
                onClick={() => { setShowQCDropdown(p => !p); setQcDropdownFilter('') }}
                className={`w-7 h-7 inline-flex items-center justify-center rounded-lg transition-all ${showQCDropdown ? 'bg-primary text-primary-foreground neu-relief' : 'neu-convex text-primary'}`}
                title="Meus comandos rápidos"
                aria-haspopup="menu"
                aria-expanded={showQCDropdown}
                aria-label="Abrir menu de comandos rápidos">
                <FiCommand className={`w-3.5 h-3.5 ${showQCDropdown ? 'text-primary-foreground' : 'text-primary'}`} />
              </button>
              {showQCDropdown && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setShowQCDropdown(false)} />
                  <div
                    ref={qcDropdownRef}
                    role="menu"
                    aria-label="Comandos rápidos"
                    onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); setShowQCDropdown(false) } }}
                    className="absolute right-0 top-full mt-1 w-72 neu-pop rounded-xl z-40 overflow-hidden"
                  >
                    <div className="px-3 py-2 border-b border-border flex items-center gap-1.5">
                      <FiCommand className="w-3 h-3 text-primary" />
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Meus Comandos</span>
                      <span className="ml-auto text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-medium">{userQuickCmds.length}</span>
                    </div>
                    {userQuickCmds.length > 5 && (
                      <div className="px-2 py-1.5 border-b border-border">
                        <div className="relative">
                          <FiSearch className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                          <input
                            autoFocus
                            type="text"
                            value={qcDropdownFilter}
                            onChange={e => setQcDropdownFilter(e.target.value)}
                            placeholder="Filtrar comandos..."
                            aria-label="Filtrar lista de comandos"
                            className="w-full pl-6 pr-2 py-1 text-[10px] rounded border border-border bg-background focus:border-border focus:ring-2 focus:ring-ring/30 outline-none"
                          />
                        </div>
                      </div>
                    )}
                    <div className="max-h-60 overflow-y-auto py-1">
                      {(() => {
                        const filtered = qcDropdownFilter.trim()
                          ? userQuickCmds.filter(c =>
                              c.label.toLowerCase().includes(qcDropdownFilter.toLowerCase()) ||
                              (c.description || '').toLowerCase().includes(qcDropdownFilter.toLowerCase()))
                          : userQuickCmds
                        if (filtered.length === 0) return (
                          <div className="px-3 py-4 text-[10px] text-muted-foreground text-center">Nenhum comando casa com o filtro.</div>
                        )
                        return filtered.map(cmd => (
                          <button key={cmd.id}
                            role="menuitem"
                            onClick={() => {
                              setShowQCDropdown(false)
                              setAiMessages([])
                              setTimeout(() => setExpandedQC(cmd.id), 100)
                            }}
                            className="w-full text-left px-3 py-2 text-[11px] hover:bg-secondary/70 focus:bg-secondary/70 focus:outline-none transition-colors flex items-center gap-2">
                            <FiZap className="w-3.5 h-3.5 text-primary shrink-0" />
                            <div className="min-w-0 flex-1">
                              <div className="font-medium text-foreground truncate">{cmd.label}</div>
                              {cmd.description && <div className="text-[10px] text-muted-foreground truncate">{cmd.description}</div>}
                            </div>
                            {cmd.variables?.length > 0 && (
                              <span className="text-[10px] text-muted-foreground/60 shrink-0">{cmd.variables.length} var</span>
                            )}
                          </button>
                        ))
                      })()}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          {/* Mesma moldura do botao de comandos (w-7 h-7 neu-convex): sobraram so
              dois nesta ponta e o retangulo antigo destoava. O hover rose continua
              avisando que a acao apaga; a vassoura e que fica amarela. */}
          {(aiMessages.length > 0 || queryResult || reportData || currentSQL || aiInput) && (
            <button
              onClick={limpaTudo}
              className="w-7 h-7 inline-flex items-center justify-center rounded-lg neu-convex transition-all hover:bg-rose-50 dark:hover:bg-rose-950"
              title="Limpa o chat, o SQL atual, a tabela e os relatórios — equivale a F5 sem recarregar"
              aria-label="Limpar chat, SQL, tabela e relatórios"
            >
              <GiBroom className="w-4 h-4 text-amber-500" />
            </button>
          )}
        </div>

        {/* Messages */}
        <div ref={aiChatRef} onScroll={handleChatScroll} onWheel={handleChatWheel} onTouchStart={handleChatTouchStart} onTouchMove={handleChatTouchMove} className="flex-1 overflow-y-auto px-3 pt-4 pb-3 space-y-4">
          {aiMessages.length === 0 ? (
            <div className="flex flex-col h-full">
              {/* Chat vazio: atalhos, não cartaz. Antes havia um ícone de 48px,
                  o título "Consulte com IA" (o painel já é isso), um parágrafo
                  explicando o óbvio e seis exemplos escritos à mão — sem nenhuma
                  telemetria, então nunca se soube se alguém clicava. No lugar
                  entram as consultas que a própria pessoa fez e os comandos que
                  ela salvou, que têm uso medido. A dica de teclado foi para o
                  placeholder do campo, onde é lida na hora certa. */}
              <div className="flex-1 flex flex-col justify-center px-1 py-2">
                {minhasRecentes.length > 0 && (
                  <div className="w-full">
                    <div className="flex items-center gap-2 mb-2 px-1">
                      <div className="h-px flex-1 bg-border/60" />
                      <span className="text-[10px] text-muted-foreground/70 uppercase tracking-widest font-semibold flex items-center gap-1.5">
                        <FiClock className="w-3 h-3" />Suas últimas consultas
                      </span>
                      <div className="h-px flex-1 bg-border/60" />
                    </div>
                    <div className="space-y-1">
                      {minhasRecentes.map(it => (
                        <button
                          key={it.id}
                          onClick={() => { setAiInput(it.user_prompt || ''); setTimeout(() => aiInputRef.current?.focus(), 50) }}
                          title={it.user_prompt || ''}
                          className="w-full text-left px-2.5 py-1.5 rounded-lg text-[11px] text-muted-foreground bg-secondary/40 border border-transparent hover:border-primary/40 hover:bg-primary/5 hover:text-foreground transition-colors truncate"
                        >{it.user_prompt}</button>
                      ))}
                    </div>
                  </div>
                )}
                {/* Só para quem ainda não tem histórico nem comandos: três
                    exemplos, não seis, e sem parágrafo. */}
                {minhasRecentes.length === 0 && userQuickCmds.length === 0 && (
                  <div className="w-full">
                    <div className="flex items-center gap-2 mb-2 px-1">
                      <div className="h-px flex-1 bg-border/60" />
                      <span className="text-[10px] text-muted-foreground/70 uppercase tracking-widest font-semibold">Para começar</span>
                      <div className="h-px flex-1 bg-border/60" />
                    </div>
                    <div className="space-y-1">
                      {[
                        'Vendas da última semana por distrito',
                        'Clientes inativos há mais de 4 semanas',
                        'Total de pedidos por grupo no mês atual',
                      ].map(p => (
                        <button
                          key={p}
                          onClick={() => { setAiInput(p); setTimeout(() => aiInputRef.current?.focus(), 50) }}
                          className="w-full text-left px-2.5 py-1.5 rounded-lg text-[11px] text-muted-foreground bg-secondary/40 border border-transparent hover:border-primary/40 hover:bg-primary/5 hover:text-foreground transition-colors truncate"
                        >{p}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* ═══ Dynamic Quick Commands (per-user) ═══ */}
              {userQuickCmds.length > 0 && (
              <div className="mt-5 w-full">
                <div className="flex items-center gap-2 mb-2.5 px-1">
                  <div className="h-px flex-1 bg-border/60" />
                  <span className="text-[10px] text-muted-foreground/70 uppercase tracking-widest font-semibold flex items-center gap-1.5">
                    <FiCommand className="w-3 h-3" />Meus comandos
                  </span>
                  <div className="h-px flex-1 bg-border/60" />
                </div>
                <div className="space-y-2">
                  {userQuickCmds.map(cmd => {
                    const isOpen = expandedQC === cmd.id
                    const params = qcParams[cmd.id] || {}
                    return (<div key={cmd.id} className={`rounded-xl border bg-card neu-item transition-all ${isOpen ? 'border-primary/40' : 'border-border hover:border-primary/40 hover:shadow-md'}`}>
                      <button onClick={() => setExpandedQC(isOpen ? null : cmd.id)}
                        className="group w-full text-left px-3 py-2.5 text-[11px] neu-item flex items-center gap-2.5">
                        <div className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors shadow-sm ${isOpen ? 'bg-primary' : 'bg-primary/85 group-hover:bg-primary'}`}>
                          <FiZap className="w-3 h-3 text-primary-foreground" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="font-medium text-foreground">{cmd.label}</span>
                          {!isOpen && cmd.description && <p className="text-[10px] text-muted-foreground/70 truncate mt-0.5">{cmd.description}</p>}
                        </div>
                        {isOpen ? <FiChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <FiChevronRight className="w-3.5 h-3.5 text-muted-foreground/50 group-hover:text-foreground shrink-0 transition-colors" />}
                      </button>
                      {isOpen && (
                        <div className="px-3 pb-3 space-y-2.5 border-t border-border/50 pt-2.5">
                          {cmd.description && <p className="text-[10px] text-muted-foreground">{cmd.description}</p>}
                          {(cmd.variables || []).length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {cmd.variables.map(v => (
                                <div key={v.key} className="flex-1 min-w-[100px]">
                                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground/80 font-semibold mb-1 block">{v.label}</label>
                                  <input type="text"
                                    value={params[v.key] ?? v.default}
                                    onChange={e => setQcParams(p => ({ ...p, [cmd.id]: { ...p[cmd.id], [v.key]: e.target.value } }))}
                                    placeholder={v.placeholder || v.default}
                                    className="w-full text-[11px] px-2 py-1.5 rounded-lg border border-dashed border-border bg-secondary/10 text-foreground placeholder:text-muted-foreground/40 focus:border-border focus:bg-secondary/30 focus:ring-2 focus:ring-ring/30 outline-none transition-colors" />
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="flex items-center gap-2 pt-1">
                            <button
                              onClick={() => { const sql = buildQCSQL(cmd, params); executeSQL(sql); trackQCUsage(cmd.id); setExpandedQC(null) }}
                              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-semibold rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground transition-colors shadow-sm">
                              <FiZap className="w-3 h-3" /> Executar
                            </button>
                            <button
                              onClick={() => { const sql = buildQCSQL(cmd, params); setCurrentSQL(sql); navigator.clipboard.writeText(sql) }}
                              className="flex items-center gap-1 px-2.5 py-2 text-[11px] rounded-lg border border-border hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                              title="Copiar SQL gerado">
                              <FiCopy className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => handleEditQC(cmd)}
                              className="p-2 text-[11px] rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                              title="Editar comando (nome, descrição, variáveis, SQL)">
                              <FiEdit2 className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => setConfirmDelete({ id: cmd.id, label: cmd.label })}
                              className="p-2 text-[11px] rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-400 transition-colors"
                              title="Excluir comando">
                              <FiTrash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>)
                  })}
                </div>
              </div>
              )}
            </div>
          ) : (
            aiMessages.map((msg, msgIdx) => {
              // Chat multi-participativo: mensagem é "minha" se o authorId casa
              // com o user logado (ou — fallback — role=user sem authorId, caso
              // antigo). Caso contrário, é de outro autor (Ricardo, Juliane...).
              const isOwn = msg.role === 'user' && (msg.authorId === user?.id || !msg.authorId)
              const palette = pickAuthorPalette(msg.authorId, isOwn)
              const displayName = msg.role === 'error' ? 'Erro'
                : msg.role === 'assistant' ? 'Assistente'
                : msg.authorName || 'Você'
              const rightAlign = isOwn
              return (
              <div key={msg.id} className={`flex gap-2 ${rightAlign ? 'flex-row-reverse' : ''}`}>
                {/* Sem mt: quem alinha é a altura fixa do cabeçalho ao lado
                    (min-h-[24px] = altura do avatar), senão o nome e a hora
                    flutuam ~6px acima do centro do círculo. */}
                <div className="shrink-0">
                  {msg.role === 'error' ? (
                    <div className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center"><FiX className="w-3 h-3 text-white" /></div>
                  ) : msg.role === 'assistant' ? (
                    <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center"><FiZap className="w-3 h-3 text-primary-foreground" /></div>
                  ) : msg.authorAvatar ? (
                    <img src={msg.authorAvatar} alt={displayName} className="w-6 h-6 rounded-full object-cover" />
                  ) : (
                    <div className={`w-6 h-6 rounded-full ${palette.bg} ${palette.text} flex items-center justify-center text-[10px] font-semibold`}>
                      {authorInitials(displayName)}
                    </div>
                  )}
                </div>
                <div className={`max-w-[88%] space-y-1.5 ${rightAlign ? 'items-end flex flex-col' : ''}`}>
                  <div className={`flex items-center gap-1.5 min-h-[24px] ${rightAlign ? 'flex-row-reverse' : ''}`}>
                    <span className={`text-[11px] font-semibold ${msg.role === 'error' ? 'text-red-700 dark:text-red-300' : msg.role === 'assistant' ? 'text-primary' : palette.name}`}>
                      {displayName}
                    </span>
                    <span className="text-[11px] text-muted-foreground">{formatTime(msg.timestamp)}</span>
                  </div>
                  {/* ESCALA TIPOGRÁFICA DA COLUNA DO CHAT — 3 níveis, só isso:
                        12px  corpo da mensagem e campo de digitação
                        11px  interface (nome, hora, rótulos, botões, SQL mono)
                        10px  micro (chips, contadores, badges)
                      Antes eram oito tamanhos (8/9/10/11/12/13 + text-xs +
                      text-sm) num intervalo de 6px: saltos de 1px não criam
                      hierarquia, só fazem o texto parecer de fontes diferentes.
                      Ao mexer aqui, escolha um dos três — não invente um quarto. */}
                  <div className={`rounded-2xl px-3 py-2 text-[12px] leading-relaxed ${
                    msg.role === 'error' ? `rounded-${rightAlign ? 'tr' : 'tl'}-md bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-200 border border-red-300 dark:border-red-700`
                    : msg.role === 'user' ? `${rightAlign ? 'rounded-tr-md' : 'rounded-tl-md'} bg-card neu-bubble-out border ${palette.border} text-foreground`
                    : 'rounded-tl-md neu-bubble-in text-foreground'
                  }`}>
                    {msg.role === 'assistant' ? (
                      <div className="chat-markdown max-w-none">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>{msg.content}</ReactMarkdown>
                      </div>
                    ) : (
                      <div className="whitespace-pre-wrap">{msg.content}</div>
                    )}
                  </div>
                  {/* Camada 8: thinking histórico colapsado — só aparece se a request veio
                      via SSE e o modelo emitiu thinking. Default colapsado pra não poluir. */}
                  {msg.role === 'assistant' && msg.thinking && (
                    <details className="rounded-md border border-border bg-secondary w-full">
                      <summary className="cursor-pointer select-none px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5">
                        <FiZap className="w-3 h-3 text-primary" />
                        Raciocínio do modelo ({msg.thinking.length.toLocaleString('pt-BR')} chars)
                      </summary>
                      <pre className="px-2 py-1.5 text-[11px] font-mono leading-relaxed whitespace-pre-wrap overflow-x-auto m-0 max-h-40 overflow-y-auto text-foreground bg-background border-t border-border">{msg.thinking}</pre>
                    </details>
                  )}
                  {/* Barra do SQL, não o SQL inteiro. A coluna do chat tem ~400px
                      úteis e o mesmo SQL já está no card central com ~1140px,
                      scroll e destaque — renderizar de novo aqui empurrava a
                      explicação para fora da tela sem nada em troca. Aqui fica só
                      a identificação; clicar no bloco abre no centro, onde é
                      legível. Sem botão "ver": o bloco inteiro é o alvo. */}
                  {msg.sql && msg.role === 'assistant' && (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => abrirSqlNoCentro(msg.sql!)}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); abrirSqlNoCentro(msg.sql!) } }}
                      title="Abrir este SQL no painel central"
                      className="w-full flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border bg-zinc-900/95 dark:bg-black/40 neu-sql-in cursor-pointer hover:border-primary/50 transition-colors"
                    >
                      {/* Trunca ANTES de destacar: passar o SQL inteiro pelo hlSQL
                          e cortar depois quebraria o HTML no meio de uma tag. */}
                      <span
                        className="flex-1 min-w-0 truncate font-mono text-[11px] text-zinc-200"
                        dangerouslySetInnerHTML={{ __html: hlSQL(resumoSQL(msg.sql)) }}
                      />
                      <button
                        onClick={e => { e.stopPropagation(); copiarSqlDaBolha(msg.id, msg.sql!) }}
                        title="Copiar SQL"
                        className="shrink-0 p-1 rounded text-zinc-400 hover:text-zinc-100 hover:bg-white/10 transition-colors"
                      >
                        {copiedMsgId === msg.id
                          ? <FiCheck className="w-3 h-3 text-emerald-400" />
                          : <FiCopy className="w-3 h-3" />}
                      </button>
                    </div>
                  )}
                  {msgIdx === idxUltimoSql && queryResult && queryResult.rows.length > 0 && (
                    <button
                      onClick={() => setFolhaAberta(true)}
                      className="xl:hidden mt-1.5 w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl neu-convex bg-card border border-border active:border-primary/50 transition-colors text-left"
                    >
                      <FiDatabase className="w-4 h-4 text-primary shrink-0" />
                      <span className="flex-1 min-w-0">
                        <span className="block text-[13px] font-medium text-foreground">
                          {queryResult.rowCount} {queryResult.rowCount === 1 ? 'linha' : 'linhas'} · {queryResult.columns.length} {queryResult.columns.length === 1 ? 'coluna' : 'colunas'}
                        </span>
                        <span className="block text-[11px] text-muted-foreground">toque para ver a tabela</span>
                      </span>
                      <FiChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                    </button>
                  )}
                  {/* Camada 7: feedback banner minimalista (1 linha) — só aparece
                      quando a SQL foi validada (validatedQueryId presente). Voto
                      vira UI inline; promoção (admin) vai direto pro vault sem
                      precisar de 3 votos. */}
                  {msg.sql && msg.role === 'assistant' && msg.validatedQueryId && (
                    <div className={`mt-1.5 px-2 py-1 rounded-md border flex items-center gap-1.5 text-[11px] whitespace-nowrap overflow-x-auto transition-all ${msg.justPromoted ? 'bg-amber-50 dark:bg-amber-950 border-amber-400 dark:border-amber-600 animate-pulse' : 'bg-secondary border-border'}`}>
                      {!msg.userVote ? (
                        <>
                          <span className="text-muted-foreground text-[10px]">Útil?</span>
                          <button
                            onClick={() => voteOnQuery(msg.id, msg.validatedQueryId!, 1)}
                            className="px-1.5 py-0.5 rounded text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900 transition-colors leading-none"
                            title="Marcar como bom — vira candidata a few-shot aprendido"
                            aria-label="Marcar resultado como bom"
                          ><FiThumbsUp className="w-3.5 h-3.5" /></button>
                          <button
                            onClick={() => voteOnQuery(msg.id, msg.validatedQueryId!, -1)}
                            className="px-1.5 py-0.5 rounded text-rose-700 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900 transition-colors leading-none"
                            title="Marcar como ruim"
                            aria-label="Marcar resultado como ruim"
                          ><FiThumbsDown className="w-3.5 h-3.5" /></button>
                          {user?.isAdmin && (
                            <button
                              onClick={() => promoteQuery(msg.id, msg.validatedQueryId!)}
                              className="px-1.5 py-0.5 rounded text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900 transition-colors leading-none"
                              title="Admin: promover direto pro vault sem precisar de 3 votos"
                              aria-label="Promover query para o vault"
                            ><FiFolderPlus className="w-3.5 h-3.5" /></button>
                          )}
                          {(typeof msg.votesUp === 'number' || typeof msg.votesDown === 'number') && ((msg.votesUp ?? 0) + (msg.votesDown ?? 0)) > 0 && (
                            <span className="text-[10px] text-muted-foreground ml-auto" title="Votos totais nesta query">
                              {msg.votesUp ?? 0}·{msg.votesDown ?? 0}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-muted-foreground flex items-center gap-1.5">
                          <span title={msg.userVote === 1 ? 'Você marcou como boa' : 'Você marcou como ruim'} className="inline-flex items-center gap-0.5">
                            {msg.userVote === 1
                              ? <><FiCheck className="w-3 h-3 text-emerald-600" /><FiThumbsUp className="w-3 h-3" /></>
                              : <><FiX className="w-3 h-3 text-rose-600" /><FiThumbsDown className="w-3 h-3" /></>}
                          </span>
                          {(typeof msg.votesUp === 'number' || typeof msg.votesDown === 'number') && (
                            <span className="text-[10px]" title="Votos totais">
                              {msg.votesUp ?? 0}·{msg.votesDown ?? 0}
                            </span>
                          )}
                          {msg.justPromoted && (
                            <span className="text-amber-600 dark:text-amber-300 font-semibold animate-bounce text-[10px]" title="Esta query foi promovida pro vault como exemplo de few-shot">
                              🎉 Aprendida
                            </span>
                          )}
                          {msg.promoted && msg.vaultSlug && (
                            <a
                              href={`/?panel=biblioteca&path=${encodeURIComponent(`conhecimento/oracle-draco/queries/${msg.vaultSlug}.md`)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-amber-600 hover:text-amber-700 text-[10px] inline-flex items-center gap-0.5"
                              title="Abrir nota da query na Biblioteca em nova aba"
                            ><FiAward className="w-3 h-3" /><span className="underline decoration-dotted hover:decoration-solid">vault</span></a>
                          )}
                          {msg.promoted && !msg.vaultSlug && (
                            <span className="text-amber-600 text-[10px] inline-flex items-center gap-0.5" title="Query promovida ao vault"><FiAward className="w-3 h-3" /> vault</span>
                          )}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              )
            })
          )}
          {/* ─── Oferta de modo ultra ───
              Aparece quando a consulta penou (correções demais, 0 linhas, ou o
              Oracle recusando). O resultado imperfeito já foi entregue acima —
              isto é um convite, não um bloqueio. Fica de pé mesmo se o usuário
              trocar de painel: o job guarda a oferta no servidor. */}
          {escalateOffer && !aiLoading && (
            <div className="flex gap-2">
              <div className="w-6 h-6 rounded-full bg-violet-500 flex items-center justify-center shrink-0">
                <FiZap className="w-3 h-3 text-white" />
              </div>
              <div className="flex-1 min-w-0 rounded-2xl rounded-tl-md px-3 py-2.5 bg-violet-50 dark:bg-violet-950/40 border border-violet-300 dark:border-violet-800">
                <p className="text-[11px] text-foreground">
                  {escalateOffer.reason === 'zero_rows'
                    ? `A consulta rodou mas não trouxe nenhuma linha`
                    : `A consulta precisou de ${escalateOffer.roundsUsed} ajustes`}
                  {' '}— quer que eu tente de novo com um modelo mais forte?
                </p>
                <p className="mt-1 text-[10px] text-muted-foreground">
                  {escalateOffer.nextTierLabel} · restam {escalateOffer.quotaRemaining} usos hoje
                </p>
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={aceitarModoUltra}
                    disabled={escalando}
                    className="text-[11px] px-2.5 py-1 rounded bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60 transition-colors"
                  >{escalando ? 'Enviando…' : `Tentar no ${escalateOffer.nextTierLabel}`}</button>
                  <button
                    onClick={() => setEscalateOffer(null)}
                    className="text-[11px] px-2.5 py-1 rounded bg-muted text-muted-foreground hover:bg-muted/70 transition-colors"
                  >Ficar com este resultado</button>
                </div>
              </div>
            </div>
          )}
          {aiLoading && (
            <div className="flex gap-2">
              <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shrink-0"><FiZap className="w-3 h-3 text-primary-foreground animate-pulse" /></div>
              <div className="flex-1 min-w-0 space-y-1.5">
                <div className="rounded-2xl rounded-tl-md px-3 py-2.5 bg-card neu-pop border border-border min-w-[200px]">
                  {/* Cronômetro + aviso aos 5min: heartbeat de 25s sincroniza com servidor. */}
                  <div className="flex items-center justify-between mb-2 text-[10px] text-muted-foreground tabular-nums">
                    <span className="flex items-center gap-1"><FiClock className="w-3 h-3" /> {Math.floor(aiElapsedMs/60000)}:{String(Math.floor((aiElapsedMs%60000)/1000)).padStart(2,'0')}</span>
                    {/* Em tier ultra o modelo é bem mais lento — dizer em qual está
                        evita que a espera maior pareça travamento. */}
                    {rotuloTier(aiTier) && (
                      <span className="text-violet-500 font-semibold">{rotuloTier(aiTier)}</span>
                    )}
                    {aiElapsedMs > 300000 && (
                      <span className="text-orange-500 font-medium">⚠ Demorando mais que o normal</span>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    {[
                      { s: 1, l: 'Conectando ao Oracle' },
                      { s: 2, l: 'Gerando SQL query' },
                      { s: 3, l: 'Processando resposta' },
                      { s: 4, l: 'Validando sintaxe SQL' },
                      { s: 5, l: 'Refinando a query' },
                      { s: 6, l: 'Testando no Oracle' },
                      { s: 7, l: 'SQL validado!' },
                    ].map(({ s, l }) => {
                      const current = aiStage?.step ?? 0
                      if (s > current) return null
                      const done = s < current
                      const active = s === current
                      return (
                        <div key={s} className={`flex items-center gap-1.5 transition-all duration-300 ${active ? 'opacity-100' : 'opacity-60'}`}>
                          {done ? (
                            <FiCheck className="w-3 h-3 text-emerald-500 shrink-0" />
                          ) : active ? (
                            <FiLoader className="w-3 h-3 text-primary animate-spin shrink-0" />
                          ) : null}
                          <span className={`text-[11px] ${done ? 'text-emerald-500' : 'text-foreground font-medium'}`}>{l}</span>
                        </div>
                      )
                    })}
                  </div>
                  {/* 60s nos tiers ultra (bem mais lentos), 5min no normal. */}
                  {aiElapsedMs > (aiTier > 0 ? 60000 : 300000) && (
                    <button
                      onClick={cancelAiGeneration}
                      className="mt-2 w-full text-[10px] py-1 rounded bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900 transition-colors"
                    >Cancelar agora</button>
                  )}
                </div>
                {/* Camada 8.6: contexto injetado no system prompt — chips com
                    fichas de tabelas DRADBA e queries aprendidas. Transparência:
                    o usuário vê o que o modelo está consumindo antes de pensar.
                    Cada chip de query aprendida abre a nota no vault em nova aba. */}
                {(injectedContext.vaultTables.length > 0 || injectedContext.learnedSlugs.length > 0) && (
                  <div className="neu-pop rounded-xl px-2.5 py-2 space-y-1.5">
                    <div className="text-[11px] text-foreground font-medium flex items-center gap-1.5"><FiDatabase className="w-3 h-3 text-primary" /> Contexto injetado no prompt</div>
                    {injectedContext.vaultTables.length > 0 && (
                      <div className="flex flex-wrap gap-1 items-center">
                        <span className="text-[11px] text-muted-foreground">Fichas DRADBA:</span>
                        {injectedContext.vaultTables.map(t => (
                          <a
                            key={t}
                            href={`/?panel=biblioteca&path=${encodeURIComponent(`conhecimento/oracle-draco/tabelas/${t}.md`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] px-1.5 py-0.5 rounded-md bg-secondary text-foreground/90 border border-border hover:bg-secondary/70 transition-colors font-mono"
                            title={`Abrir ficha [[${t}]] na Biblioteca`}
                          >{t}</a>
                        ))}
                      </div>
                    )}
                    {injectedContext.learnedSlugs.length > 0 && (
                      <div className="flex flex-wrap gap-1 items-center">
                        <span className="text-[11px] text-muted-foreground">Aprendidas:</span>
                        {injectedContext.learnedSlugs.map(slug => (
                          <a
                            key={slug}
                            href={`/?panel=biblioteca&path=${encodeURIComponent(`conhecimento/oracle-draco/queries/${slug}.md`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] px-1.5 py-0.5 rounded-md bg-primary/10 text-primary border border-primary/25 hover:bg-primary/15 transition-colors inline-flex items-center gap-1"
                            title={`Abrir query aprendida na Biblioteca`}
                          ><FiAward className="w-3 h-3 shrink-0" />{slug.replace(/^\d{4}-\d{2}-\d{2}-/, '').slice(0, 40)}</a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {/* Camada 8: painel de thinking ao vivo. Aparece assim que o modelo
                    começa a raciocinar, scroll automático no fim, colapsável. */}
                {streamingThinking && (
                  <div className="rounded-md border border-indigo-500 bg-indigo-50 dark:bg-indigo-950 overflow-hidden">
                    <button
                      onClick={() => setThinkingOpen(o => !o)}
                      className="w-full flex items-center justify-between px-2.5 py-1.5 text-[11px] hover:bg-indigo-100 dark:hover:bg-indigo-900 transition-colors"
                    >
                      <span className="flex items-center gap-1.5 text-indigo-700 dark:text-indigo-300 font-medium">
                        <FiZap className="w-3 h-3 animate-pulse" />
                        Raciocínio ao vivo · {streamingThinking.length.toLocaleString('pt-BR')} chars
                      </span>
                      <span className="text-muted-foreground">{thinkingOpen ? '▾' : '▸'}</span>
                    </button>
                    {thinkingOpen && (
                      <pre
                        ref={el => { if (el) el.scrollTop = el.scrollHeight }}
                        className="px-2.5 py-2 text-[11px] font-mono leading-relaxed whitespace-pre-wrap overflow-x-auto m-0 max-h-40 overflow-y-auto text-indigo-900 dark:text-indigo-100 bg-indigo-50 dark:bg-indigo-950 border-t border-indigo-500"
                      >{streamingThinking}</pre>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="px-3 py-3 border-t border-border glass-bar shrink-0">
          {/* A BOX é o container, não o textarea. O textarea fica transparente e
              sem borda por dentro, e a ação mora no canto inferior direito como
              ícone — antes era um botão de 48px ao lado, que numa coluna de
              ~400px comia largura útil de digitação e competia em peso com a
              própria caixa. O anel de foco vai no container, para caixa e ação
              acenderem como uma peça só.
              A dica de teclado saiu do placeholder: ficava lá o tempo todo
              poluindo, e o atalho segue no title do botão — onde se procura. */}
          {/* neu-inset não é enfeite, é obrigatório: dentro de .neu-lab um
              `bg-background` renderiza MAIS CLARO que a base neu e vira um bloco
              branco na coluna — o próprio index.css avisa disso, junto de
              bg-secondary/bg-muted/glass-bar. As classes Tailwind ficam como
              base para quando o tema neu está desligado; dentro dele, neu-inset
              afunda o campo, que é a gramática certa para entrada de texto.
              rows={2} NÃO é acaso: a altura do rodapé do chat precisa casar com
              a do rodapé da sidebar principal, e cada linha a mais desloca a
              divisória em ~19px. A caixa ganhou espaço na LARGURA, ao perder o
              botão de 48px que ficava ao lado — não na altura.
              O `block` do textarea também não é decorativo: textarea nasce
              inline-block, se assenta na linha de base e o navegador reserva
              espaço para os descendentes ABAIXO dela. Esse vão fantasma fica
              dentro da borda e empurra o conteúdo para cima, como se a caixa
              não encostasse embaixo. `display:block` tira o baseline da conta. */}
          {/* Abaixo de 1280px a caixa vira uma LINHA: [texto][🎤][➤]. Em
              `pr-*` os botões roubariam ~100px de TODAS as linhas do textarea,
              e numa tela de 390px isso é um quarto da largura útil. Em xl volta
              a ser o container `block` com a ação absoluta no canto, exatamente
              como sempre foi. */}
          <div className={`relative flex items-end gap-2 xl:block xl:gap-0 ${aiLoading || connectionStatus !== 'connected' ? 'opacity-40' : ''}`}>
            <div className={`flex-1 min-w-0 rounded-2xl bg-background border border-border neu-inset transition-shadow ${
              composerFocused ? 'ring-2 ring-ring/40' : ''
            }`}>
            {/* text-base (16px) abaixo de xl NÃO é escolha estética: com fonte
                menor que 16px o Safari do iPhone/iPad dá zoom sozinho ao focar o
                campo e desalinha a tela inteira, e o usuário tem que pinçar de
                volta a cada pergunta. Em xl volta aos 12px do desenho original. */}
            <textarea ref={aiInputRef} value={aiInput} onChange={e => setAiInput(e.target.value)} onKeyDown={handleAiKeyDown}
              onFocus={() => setComposerFocused(true)} onBlur={() => setComposerFocused(false)}
              placeholder="Descreva a consulta desejada…" rows={estreito ? 1 : 2}
              disabled={aiLoading || connectionStatus !== 'connected'}
              className="block w-full resize-none bg-transparent px-3.5 py-3 pr-3.5 xl:pr-12 text-base xl:text-[12px] rounded-2xl focus:outline-none disabled:cursor-not-allowed placeholder:text-muted-foreground/50 leading-relaxed" />
            </div>
            {/* 44px é o alvo de toque mínimo; 32px (w-8) é confortável com mouse
                e escorregadio com o dedo. E abaixo de xl o microfone e o enviar
                são DOIS botões, os dois sempre à vista: no desenho antigo um
                único canto de 32px acumulava quatro papéis e o enviar só
                aparecia depois que a caixa ganhava foco. */}
            {/* 9px, não 8. A ação era `right-2 bottom-2` dentro da caixa, cujo
                padding box já começa DEPOIS da borda de 1px; agora ela se ancora
                no invólucro, que não tem borda. Sem o +1 o botão desce e anda
                para a direita 1px no desktop — medido em 780 pixels de diferença
                contra o build anterior, com o teste tendo ruído zero. */}
            <div className="shrink-0 flex items-center pb-1 xl:pb-0 xl:absolute xl:right-[9px] xl:bottom-[9px]">
              {aiLoading ? (
                <button onClick={cancelAiGeneration}
                  className="w-11 h-11 xl:w-8 xl:h-8 shrink-0 rounded-full neu-send-danger bg-red-500 text-white hover:bg-red-600 transition-colors flex items-center justify-center"
                  title="Cancelar geração (Esc)">
                  <FiX className="w-5 h-5 xl:w-4 xl:h-4" />
                </button>
              ) : recState === 'recording' ? (
                <button onClick={stopRecording}
                  className="w-11 h-11 xl:w-8 xl:h-8 shrink-0 rounded-full neu-send-danger bg-red-500 text-white hover:bg-red-600 transition-colors flex items-center justify-center"
                  title="Parar a gravação e transcrever">
                  <FiSquare className="w-4 h-4 xl:w-3.5 xl:h-3.5 animate-pulse" />
                </button>
              ) : recState === 'transcribing' ? (
                <button disabled
                  className="w-11 h-11 xl:w-8 xl:h-8 shrink-0 rounded-full neu-send bg-primary/60 text-primary-foreground flex items-center justify-center cursor-wait"
                  title="Transcrevendo áudio…">
                  <FiLoader className="w-5 h-5 xl:w-4 xl:h-4 animate-spin" />
                </button>
              ) : (aiInput.trim() || (!estreito && composerFocused)) ? (
                <button onClick={generateSqlFromAi} disabled={!aiInput.trim() || connectionStatus !== 'connected'}
                  className="w-11 h-11 xl:w-8 xl:h-8 shrink-0 rounded-full bg-primary text-primary-foreground neu-send hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  title="Gerar e executar (Enter · Shift+Enter para quebra de linha)"
                  aria-label="Gerar e executar a consulta">
                  <FiSend className="w-5 h-5 xl:w-4 xl:h-4" />
                </button>
              ) : (
                <button onClick={startRecording} disabled={connectionStatus !== 'connected'}
                  className="w-11 h-11 xl:w-8 xl:h-8 shrink-0 rounded-full bg-primary text-primary-foreground neu-send hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  title="Ditar a consulta por voz — o áudio vira texto na caixa para você conferir antes de enviar"
                  aria-label="Ditar a consulta por voz">
                  <FiMic className="w-5 h-5 xl:w-4 xl:h-4" />
                </button>
              )}
            </div>
          </div>
          {(recState !== 'idle' || recError) && (
            <div className={`mt-1.5 text-[12px] xl:text-[10px] flex items-center gap-1.5 ${recError || recState === 'recording' ? 'text-red-500' : 'text-muted-foreground'}`}>
              {recError ? (<><FiX className="w-3 h-3 shrink-0" /> {recError}</>)
                : recState === 'recording' ? (<><span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse shrink-0" /> Gravando — {recSecs}s · toque no quadrado para transcrever</>)
                : (<><FiLoader className="w-3 h-3 animate-spin shrink-0" /> Transcrevendo áudio…</>)}
            </div>
          )}
        </div>
        </>) : (
          <button onClick={() => setChatOpen(true)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors" title="Expandir assistente">
            <FiZap className="w-4 h-4 text-primary" />
          </button>
        )}
      </div>

      {/* ═══ SAVE / EDIT QUICK COMMAND MODAL ═══ */}
      {showSaveQC && saveQCData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center glass-overlay" onClick={closeSaveQCModal}>
          <div className="glass-panel neu-drawer neu-forms rounded-2xl w-[460px] max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
              {editingQCId !== null ? <FiEdit2 className="w-4 h-4 text-primary" /> : <FiPlusCircle className="w-4 h-4 text-primary" />}
              <span className="text-sm font-semibold text-foreground">{editingQCId !== null ? 'Editar Comando Rápido' : 'Salvar como Comando Rápido'}</span>
              <button onClick={closeSaveQCModal} className="ml-auto p-1 rounded hover:bg-secondary text-muted-foreground"><FiX className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1 block">Nome do comando</label>
                <input type="text" value={saveQCData.label}
                  onChange={e => setSaveQCData({ ...saveQCData, label: e.target.value })}
                  className="w-full text-sm px-3 py-2 rounded-lg border border-border bg-background focus:border-border focus:ring-2 focus:ring-ring/30 outline-none" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1 block">Descrição <span className="opacity-50 normal-case">(opcional)</span></label>
                <input type="text" value={saveQCData.description}
                  onChange={e => setSaveQCData({ ...saveQCData, description: e.target.value })}
                  placeholder="O que este comando faz..."
                  className="w-full text-sm px-3 py-2 rounded-lg border border-border bg-background focus:border-border focus:ring-2 focus:ring-ring/30 outline-none placeholder:text-muted-foreground/40" />
              </div>
              {/* Ícone fixo - sem seletor de emoji */}
              {saveQCData.variables.length > 0 && (
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5 block flex items-center gap-1">
                    <FiZap className="w-3 h-3 text-primary" /> Variáveis detectadas
                  </label>
                  <div className="space-y-2">
                    {saveQCData.variables.map((v, i) => (
                      <div key={v.key} className="flex items-center gap-2 bg-secondary/30 rounded-lg p-2">
                        <span className="text-[10px] font-mono text-primary bg-primary/10 px-1.5 py-0.5 rounded">{`{{${v.key}}}`}</span>
                        <input type="text" value={v.label}
                          onChange={e => {
                            const vars = [...saveQCData.variables]
                            vars[i] = { ...vars[i], label: e.target.value }
                            setSaveQCData({ ...saveQCData, variables: vars })
                          }}
                          className="flex-1 text-[11px] px-2 py-1 rounded border border-border bg-background focus:border-primary/50 outline-none"
                          placeholder="Label da variável" />
                        <span className="text-[9px] text-muted-foreground">padrão: {v.default}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1 block flex items-center gap-1.5">
                  SQL Template
                  {editingQCId !== null && saveQCData.variables.length > 0 && (
                    <span className="text-[9px] normal-case text-primary font-normal">
                      mantenha {`{{nome_var}}`} pras variáveis funcionarem
                    </span>
                  )}
                </label>
                {editingQCId !== null ? (
                  <textarea
                    value={saveQCData.sqlTemplate}
                    onChange={e => setSaveQCData({ ...saveQCData, sqlTemplate: e.target.value })}
                    spellCheck={false}
                    rows={8}
                    className="w-full text-[10px] font-mono p-2 rounded-lg bg-zinc-900/95 dark:bg-black/40 text-zinc-200 border border-border focus:border-border focus:ring-2 focus:ring-ring/30 outline-none leading-relaxed resize-y"
                  />
                ) : (
                  <pre className="text-[10px] font-mono p-2 rounded-lg bg-[#1e1e1e] text-[#d4d4d4] whitespace-pre-wrap break-words overflow-x-auto max-h-24 leading-relaxed">{saveQCData.sqlTemplate}</pre>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 px-4 py-3 border-t border-border">
              <button onClick={closeSaveQCModal}
                className="flex-1 px-3 py-2 text-[11px] rounded-lg border border-border hover:bg-secondary text-muted-foreground transition-colors">
                Cancelar
              </button>
              <button onClick={confirmSaveQC} disabled={!saveQCData.label.trim()}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-semibold rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-40 transition-colors">
                {editingQCId !== null ? <><FiEdit2 className="w-3 h-3" /> Salvar Alterações</> : <><FiPlusCircle className="w-3 h-3" /> Salvar Comando</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ CONFIRM DELETE MODAL ═══ */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center glass-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="glass-panel rounded-2xl w-[360px] overflow-hidden animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
            <div className="p-5 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-500/10 flex items-center justify-center">
                <FiTrash2 className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="text-sm font-semibold text-foreground mb-1">Excluir comando rápido</h3>
              <p className="text-[12px] text-muted-foreground leading-relaxed">
                Tem certeza que deseja excluir<br />
                <span className="font-medium text-foreground">"{confirmDelete.label}"</span>?
              </p>
            </div>
            <div className="flex border-t border-border">
              <button onClick={() => setConfirmDelete(null)}
                className="flex-1 px-4 py-2.5 text-[12px] font-medium text-muted-foreground hover:bg-secondary transition-colors">
                Cancelar
              </button>
              <div className="w-px bg-border" />
              <button onClick={() => { deleteQC(confirmDelete.id); setConfirmDelete(null) }}
                className="flex-1 px-4 py-2.5 text-[12px] font-medium text-red-500 hover:bg-red-500/10 transition-colors">
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cartão do relatório PDF — fixo no canto de baixo. A faixa inline de
          10px que existia antes passava despercebida: quem pediu o relatório
          precisa ver que ele ficou pronto sem procurar. Some quando o usuário
          dispensa, nunca sozinho. */}
      {(pdfJob || pdfError) && (
        <div className="fixed bottom-4 right-4 z-50 w-[340px] max-w-[calc(100vw-2rem)]" role="status" aria-live="polite">
          <div className={`rounded-xl border shadow-lg backdrop-blur-sm p-3.5 ${
            pdfCard === 'done' ? 'bg-emerald-50/95 dark:bg-emerald-950/90 border-emerald-500/40'
            : pdfCard === 'error' ? 'bg-red-50/95 dark:bg-red-950/90 border-red-500/40'
            : 'bg-card/95 border-amber-500/40'}`}>

            <div className="flex items-start gap-2.5">
              <div className={`w-8 h-8 shrink-0 rounded-lg inline-flex items-center justify-center ${
                pdfCard === 'done' ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                : pdfCard === 'error' ? 'bg-red-500/15 text-red-500'
                : 'bg-amber-500/15 text-amber-500'}`}>
                {pdfCard === 'done' ? <FiCheck className="w-4 h-4" />
                  : pdfCard === 'error' ? <FiAlertTriangle className="w-4 h-4" />
                  : <FiLoader className="w-4 h-4 animate-spin" />}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-[12.5px] font-semibold ${
                    pdfCard === 'done' ? 'text-emerald-700 dark:text-emerald-300'
                    : pdfCard === 'error' ? 'text-red-600 dark:text-red-400'
                    : 'text-foreground'}`}>
                    {pdfCard === 'done' ? 'Relatório pronto'
                      : pdfCard === 'error' ? 'Não deu para gerar'
                      : 'Gerando relatório PDF'}
                  </span>
                  {pdfCard === 'running'
                    ? <span className="text-[11px] tabular-nums text-amber-500 font-medium">{pdfSegundos}s</span>
                    : <button onClick={() => { setPdfError(null); dispensarRelatorio() }} aria-label="Dispensar"
                        className="text-muted-foreground hover:text-foreground shrink-0"><FiX className="w-3.5 h-3.5" /></button>}
                </div>

                {pdfCard === 'running' && (
                  <>
                    <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{pdfJob?.stageLabel || 'Preparando…'}</p>
                    <div className="mt-2 h-1.5 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full bg-amber-500 rounded-full transition-all duration-500"
                        style={{ width: `${Math.max(4, Math.min(100, pdfJob?.progress || 0))}%` }} />
                    </div>
                    <p className="text-[10.5px] text-muted-foreground mt-1.5">Pode sair do painel — continua rodando aqui.</p>
                  </>
                )}

                {pdfCard === 'done' && (
                  <>
                    <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2 break-words">{pdfJob?.titulo || 'Relatório do Painel Dados'}</p>
                    {pdfJob?.jaExiste && (
                      <p className="text-[10.5px] text-emerald-700/80 dark:text-emerald-400/80 mt-1">
                        Já estava pronto ({pdfQuando(pdfJob.idadeMin)}) — não precisou gerar de novo.
                      </p>
                    )}
                    <div className="mt-2.5 flex items-center gap-1.5">
                      <button onClick={() => baixarRelatorio(pdfJob)} disabled={pdfBaixando}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold
                          bg-emerald-600 hover:bg-emerald-700 text-white transition-colors disabled:opacity-60">
                        {pdfBaixando ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiDownload className="w-3.5 h-3.5" />}
                        {pdfBaixando ? 'Baixando…' : 'Baixar PDF'}
                      </button>
                      <button onClick={() => exportReportPDF(true)} disabled={pdfBaixando || !queryResult?.rows?.length}
                        title="Descartar este e gerar um relatório novo com os dados na tela"
                        className="px-2.5 py-2 rounded-lg text-[11px] font-medium border border-emerald-600/40
                          text-emerald-700 dark:text-emerald-300 hover:bg-emerald-600/10 transition-colors disabled:opacity-40">
                        Gerar de novo
                      </button>
                    </div>
                    {pdfAviso && <p className="text-[10.5px] text-muted-foreground mt-2 leading-snug">{pdfAviso}</p>}
                  </>
                )}

                {pdfCard === 'error' && (
                  <p className="text-[11px] text-red-600/90 dark:text-red-400/90 mt-0.5 leading-snug">{pdfJob?.error || pdfError}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}


      {user?.isAdmin && (
        <RevisaoAprendizado
          isOpen={revisaoAberta}
          onClose={() => setRevisaoAberta(false)}
          authenticatedFetch={authenticatedFetch}
          onContagem={setRevisaoN}
        />
      )}
    </div>
  )
}
